import React, { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import { createVehiclesForPerson, describeVehicleFailures, type VehicleCreateFailure } from '../../services/personVehicles';
import { TopBar } from './components/TopBar';
import { CustomerTabs } from './components/CustomerTabs';
import { CloseTabConfirmation } from './components/CloseTabConfirmation';
import { ActionMenuBar } from './components/ActionMenuBar';
import { QuickSearchBar } from './components/QuickSearchBar';
import { MainContentArea } from './components/MainContentArea';
import { LocationDetailView } from './components/LocationDetailView';
import { EventsPanel } from './components/EventsPanel';
import { Botoeira } from './components/Botoeira';
import { DeviceActionMenu } from './components/DeviceActionMenu';
import { CustomerNotesBanner } from './components/CustomerNotesBanner';
import { LembretesBanner } from './components/LembretesBanner';
import { AddNoteModal } from './components/AddNoteModal';
import { NotesHistoryModal } from './components/NotesHistoryModal';
import { OperationalConfirmModal } from './components/OperationalConfirmModal';
import { RevokeReasonModal } from './components/RevokeReasonModal';
import { ExpiredVisitorsView } from './components/ExpiredVisitorsView';
import { DeviceAuditView } from './components/DeviceAuditView';
import PreRegistrationsPanel from './components/PreRegistrationsPanel';
import AccountRequestsPanel from './components/AccountRequestsPanel';
import EncomendaPanel from './components/EncomendaPanel';
import LivroPanel from './components/LivroPanel';
import { canManageLivro } from './components/livroThread';
import { listEncomendas } from '../../services/encomendaApi';
import { useCustomerNotes, NoteScope } from '../../hooks/useCustomerNotes';
import { ToastContainer } from '../ui/Toast';
import { toast as hotToast } from 'react-hot-toast';
import { CloseTabConfirmation as CloseTabConfirmationType, Device, OpenTab, Event, Visitor, Condo } from './types';
import { useNotifications } from '../../hooks/useNotifications';
import { useCustomer } from '../../contexts/CustomerContext';
import { usePersonDetailContext } from '../../contexts/PersonDetailContext';
import { useKeycloak } from '../../contexts/KeycloakContext';
import { useOperationalSettings } from '../../contexts/OperationalSettingsContext';
import { fetchWithAuth, condoApi, api } from '../../services/api';
import { fetchPersonDevices, revokeExitDevices } from '../../utils/deviceRevoke';
import { logger } from '../../services/logger';
import { isElectron } from '../../utils/platform';
import { deviceCommunicationService } from '../../services/deviceCommunication';
import { triggerDeviceDoor } from '../../utils/deviceDoorTrigger';
import VisitorRegistrationModal from '../VisitorRegistrationModal';
import { ResidentRegistrationModal } from '../ResidentRegistrationModal';
import EmployeeRegistrationModal from '../EmployeeRegistrationModal';
import { ArrowLeft, Pin } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useOperationalCustomers, OperationalCustomer } from '../../hooks/useOperationalCustomers';
import { useVisitorWebSocket, OperationalVisitor } from '../../hooks/useVisitorWebSocket';
import { useGlobalEventStream, OperationalEvent } from '../../hooks/useGlobalEventStream';
import { useCustomerDevicesAndZones } from '../../hooks/useCustomerDevicesAndZones';
import { usePorteiroEventStream, PorteiroEvent } from '../../hooks/usePorteiroEventStream';
import { useVisitorExitDetection } from '../../hooks/useVisitorExitDetection';
import { useMultiDeviceExitDetection } from '../../hooks/useMultiDeviceExitDetection';
import { useUnifiedExitDetection } from '../../hooks/useUnifiedExitDetection';
import { useDeferredDeviceRemovals } from '../../hooks/useDeferredDeviceRemovals';
import { useExpiredVisitorAutoRevoke } from '../../hooks/useExpiredVisitorAutoRevoke';
import { useAwaitingEntryGuard } from '../../hooks/useAwaitingEntryGuard';
import { loadDeviceOrder, saveDeviceOrder, clearDeviceOrder } from './utils/deviceOrderStorage';
import { useDeviceHealthCheck } from '../../hooks/useDeviceHealthCheck';
import { ZoneOccupancyView } from './components/ZoneOccupancyView';
import { AreasComuns } from './components/AreasComuns';
import NormasPanel from './components/NormasPanel';
import OcorrenciasPanel from './components/OcorrenciasPanel';
import SolicitacoesPanel from './components/SolicitacoesPanel';
import MuralAnunciosPanel from './components/MuralAnunciosPanel';
import AvisosPanel from './components/AvisosPanel';
import { HelpCenterPanel } from './components/HelpCenterModal';
import ManuaisPanel from './components/ManuaisPanel';
import { notify } from '../../utils/smartNotification';
import { resolvePersonPhoto } from '../../utils/personPhotoCache';
import { makeAbsolutePhotoUrl } from '../../services/api';
import { ExitDetectionDebugPanel, useDebugLogs } from './components/ExitDetectionDebugPanel';
import { appendIdentityPhoto } from '../../services/identityPhotoUpload';
import { foreignerPayload } from '../../utils/foreigner';

// Lazy load PersonProfile for person-profile view
const PersonProfile = lazy(() => import('../../pages/PersonProfile'));
const EventosGerais = lazy(() => import('../../pages/EventosGerais'));

// Custom scrollbar styles - Always visible
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #1f2937;
    border-radius: 5px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #4b5563;
    border-radius: 5px;
    border: 2px solid #1f2937;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }
  .custom-scrollbar {
    scrollbar-width: auto;
    scrollbar-color: #4b5563 #1f2937;
  }

  /* Disable tab navigation on all elements except search inputs */
  .operational-view-container button,
  .operational-view-container a,
  .operational-view-container [role="button"],
  .operational-view-container [role="tab"],
  .operational-view-container [onClick] {
    -moz-user-focus: ignore !important;
  }
`;

interface OperationalViewProps {
  onExitOperationalMode: () => void;
}

// LocalStorage keys for tab persistence
const STORAGE_KEY_OPEN_TABS = 'panther_operational_open_tabs';
const STORAGE_KEY_ACTIVE_TAB = 'panther_operational_active_tab';

// Helper to load tabs from localStorage
const loadSavedTabs = (): OpenTab[] => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_OPEN_TABS);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (e) {
    logger.warn('operational.view', 'Failed to load saved tabs from localStorage');
  }
  return [];
};

// Helper to load active tab from localStorage
const loadSavedActiveTab = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY_ACTIVE_TAB);
  } catch (e) {
    return null;
  }
};

const OperationalView: React.FC<OperationalViewProps> = ({ onExitOperationalMode }) => {
  const { selectedCustomer, availableCustomers, refreshCustomers } = useCustomer();
  const { toasts, showSuccess, showWarning, showError, removeNotification } = useNotifications();
  const { openPersonDetail, setOnVisitProfile, setOnUnitClick, setOnDepartmentClick, setOnPersonUpdate } = usePersonDetailContext();
  const { user, hasAnyRole, roles: keycloakRoles } = useKeycloak();
  const isPorteiroUser = hasAnyRole('porteiro', 'condo_staff', 'zelador');
  const { shouldUseMainFrame, settings: opSettings } = useOperationalSettings();
  const isLightTheme = opSettings.theme === 'light';
  const isSwappedLayout = opSettings.layout === 'swapped';
  const operatorName = user?.name || user?.username || 'Operador';
  const operationalViewRef = useRef<HTMLDivElement>(null);

  // Inject a <style> tag to force light colors on the react-hot-toast Toaster
  // (which renders outside .op-light at document body level).
  // Direct DOM injection is more reliable than cross-component event propagation.
  React.useEffect(() => {
    const styleId = 'panther-light-toast-override';
    if (isLightTheme) {
      let el = document.getElementById(styleId) as HTMLStyleElement | null;
      if (!el) {
        el = document.createElement('style');
        el.id = styleId;
        document.head.appendChild(el);
      }
      el.textContent = `
        [data-rht-toaster] > div > div,
        [data-rht-toaster] > div > span {
          background: #ffffff !important;
          color: #111827 !important;
          border: 1px solid #d1d5db !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.12) !important;
        }
      `;
    } else {
      document.getElementById(styleId)?.remove();
    }
    return () => { document.getElementById(styleId)?.remove(); };
  }, [isLightTheme]);

  // Also keep the body class for legacy CSS backup
  React.useEffect(() => {
    document.body.classList.toggle('panther-light-toast', isLightTheme);
    return () => { document.body.classList.remove('panther-light-toast'); };
  }, [isLightTheme]);

  // Check if user is super_admin (required for debug panel only)
  const isSuperAdmin = hasAnyRole('super_admin');
  // Mirrors the server-side gate on /credential-requests (admin | supervisor | sindico).
  const canManageSolicitacoes = hasAnyRole('admin', 'supervisor', 'sindico', 'super_admin');

  const [menuOpen, setMenuOpen] = useState(false);
  const [condoSelectorOpen, setCondoSelectorOpen] = useState(false);
  const [condoSearchTerm, setCondoSearchTerm] = useState('');

  // Tab management with customer IDs - initialize from localStorage
  const [openTabs, setOpenTabs] = useState<OpenTab[]>(loadSavedTabs);
  const [activeTabId, setActiveTabId] = useState<string | null>(loadSavedActiveTab);
  const [tabsRestored, setTabsRestored] = useState(false);

  const [closeTabConfirmation, setCloseTabConfirmation] = useState<CloseTabConfirmationType | null>(null);
  const [cadastrosMenuOpen, setCadastrosMenuOpen] = useState(false);
  // Lembretes (reminders): `showLembrete` only matters while there are zero lembretes for
  // the active customer — it lets an operator open the empty banner to add the first one,
  // and close it again if they don't want to. The moment at least one lembrete exists,
  // LembretesBanner forces itself visible regardless of this flag and hides its own close
  // control — shared reminders (ramal, zelador, síndico, etc.) must not be dismissible once
  // they exist. `lembreteCount` is reported back by the banner (which owns the live fetch)
  // so the action-bar toggle button itself can disappear once there's nothing to toggle.
  const [showLembrete, setShowLembrete] = useState(false);
  const [lembreteCount, setLembreteCount] = useState(0);
  const [currentView, setCurrentView] = useState<'visitors' | 'cadastro' | 'history' | 'location-detail' | 'person-profile' | 'register-visitor' | 'register-resident' | 'expired-visitors' | 'device-audit' | 'encomendas' | 'eventos-gerais' | 'zone-occupancy' | 'pre-registrations' | 'areas-comuns' | 'normas-internas' | 'ocorrencias' | 'mural-anuncios' | 'avisos' | 'help-center' | 'manuais' | 'livro' | 'solicitacoes'>('visitors');
  const [previousView, setPreviousView] = useState<'visitors' | 'cadastro' | 'history' | 'location-detail' | 'person-profile' | 'register-visitor' | 'register-resident' | 'expired-visitors' | 'device-audit' | 'encomendas' | 'eventos-gerais' | 'zone-occupancy' | 'pre-registrations' | 'areas-comuns' | 'normas-internas' | 'ocorrencias' | 'mural-anuncios' | 'avisos' | 'help-center' | 'manuais' | 'livro' | 'solicitacoes'>('visitors');
  const [selectedCadastro, setSelectedCadastro] = useState<string | null>(null);
  const [selectedProfilePersonId, setSelectedProfilePersonId] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<{ id: string; type: 'unit' | 'department' | 'block'; name?: string; parentBlockId?: string } | null>(null);
  const [activeDeviceMenu, setActiveDeviceMenu] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number; placement?: 'top' | 'right' } | null>(null);
  const [customerEventsCollapsed, setCustomerEventsCollapsed] = useState(false);
  const [generalEventsCollapsed, setGeneralEventsCollapsed] = useState(false);
  const [deviceStates, setDeviceStates] = useState<Record<string, { state: 'idle' | 'loading' | 'success' | 'error'; action: 'trigger' | 'test' }>>({});
  const [selectedDeviceHistory, setSelectedDeviceHistory] = useState<Device | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [isDeviceEditMode, setIsDeviceEditMode] = useState(false);
  const [pendingDeviceOrder, setPendingDeviceOrder] = useState<Device[]>([]);
  // Server-persisted botoeira order for the active customer (per-user, per-customer — see deviceOrderStorage.ts)
  const [savedDeviceOrder, setSavedDeviceOrder] = useState<string[] | null>(null);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [showVisitorModal, setShowVisitorModal] = useState(false);
  const [visitorPreRegToken, setVisitorPreRegToken] = useState<string | undefined>(undefined);
  const [visitorModalInitialStep, setVisitorModalInitialStep] = useState<'search' | 'form'>('search');
  const [showResidentModal, setShowResidentModal] = useState(false);
  const [isCreatingResident, setIsCreatingResident] = useState(false);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [isCreatingEmployee, setIsCreatingEmployee] = useState(false);
  const [employeeDepartments, setEmployeeDepartments] = useState<Array<{ id: string; name: string }>>([]);
  const [blocks, setBlocks] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [residentModalInitialUnit, setResidentModalInitialUnit] = useState<{ unitId: string; blockId: string } | null>(null);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [showNotesHistoryModal, setShowNotesHistoryModal] = useState(false);
  const [showPreRegistrationsPanel, setShowPreRegistrationsPanel] = useState(false);
  const [preRegistrationsCount, setPreRegistrationsCount] = useState(0);
  const [showAccountRequestsPanel, setShowAccountRequestsPanel] = useState(false);
  const [accountRequestsCount, setAccountRequestsCount] = useState(0);
  const [encomendaPendingCount, setEncomendaPendingCount] = useState(0);
  const [solicitacoesPendingCount, setSolicitacoesPendingCount] = useState(0);
  const [notesRefreshTrigger, setNotesRefreshTrigger] = useState(0);
  const [residentsRefreshTrigger, setResidentsRefreshTrigger] = useState(0);
  const [cadastroRefreshTrigger, setCadastroRefreshTrigger] = useState(0);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [prefilledVisitorData, setPrefilledVisitorData] = useState<{
    cpf?: string;
    firstName?: string;
    lastName?: string;
  } | undefined>(undefined);
  const [prefilledVisitorPersonId, setPrefilledVisitorPersonId] = useState<string | undefined>(undefined);
  const [prefilledVisitorPerson, setPrefilledVisitorPerson] = useState<any>(undefined);
  const [visitorModalInitialUnit, setVisitorModalInitialUnit] = useState<{ blockId: string; unitId: string } | null>(null);
  const [visitorModalInitialDepartmentId, setVisitorModalInitialDepartmentId] = useState<string | undefined>(undefined);
  const [preRegistrationComment, setPreRegistrationComment] = useState<string | undefined>(undefined);

  // Registration context for main frame mode
  const [registrationContext, setRegistrationContext] = useState<{
    type: 'visitor' | 'resident';
    initialData?: any;
    initialUnit?: { blockId: string; unitId: string };
    prefilledPersonId?: string;
    prefilledPerson?: any;
  } | null>(null);

  // Check if debug mode is enabled (Electron + dev mode)
  // Debug mode is only available to super_admin in Electron dev
  const isDebugModeEnabled = isElectron() && process.env.NODE_ENV === 'development' && isSuperAdmin;

  // Debug panel state (only for super_admin in Electron dev) - closed by default
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const { logs: debugLogs, clearLogs: clearDebugLogs } = useDebugLogs();

  // Revoke reason modal state
  const [revokeModal, setRevokeModal] = useState<{ isOpen: boolean; visitor: Visitor | null }>({
    isOpen: false,
    visitor: null,
  });

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    confirmButtonClass: string;
    icon: 'warning' | 'entry' | 'exit' | 'revoke';
    onConfirm: () => void;
    isProcessing: boolean;
    showDisclaimer?: boolean;
    disclaimerType?: 'entry' | 'revoke' | 'general';
    operatorName?: string;
    requireAcknowledgment?: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirmar',
    confirmButtonClass: 'bg-blue-600 hover:bg-blue-700',
    icon: 'warning',
    onConfirm: () => {},
    isProcessing: false,
    showDisclaimer: false,
    disclaimerType: 'general',
    operatorName: undefined,
    requireAcknowledgment: false,
  });

  // Refresh customer data on mount so feature flags (pets, livro, common_areas, etc.) are always current
  useEffect(() => {
    refreshCustomers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // Show debug panel when debug mode is enabled (super_admin only, run once on mount)
  useEffect(() => {
    const debugEnabled = localStorage.getItem('exitDetectionDebug') === 'true';
    if (debugEnabled && isElectron() && process.env.NODE_ENV === 'development' && isSuperAdmin) {
      setShowDebugPanel(true);
    }
  }, [isSuperAdmin]); // Re-run if super_admin status changes

  // Escape key handler for debug panel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Toggle debug panel with Ctrl+Shift+D (only in debug mode)
      if (e.ctrlKey && e.shiftKey && e.key === 'D' && isDebugModeEnabled) {
        e.preventDefault();
        setShowDebugPanel(prev => !prev);
      }
      // Close debug panel with Escape
      if (e.key === 'Escape' && showDebugPanel) {
        setShowDebugPanel(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showDebugPanel, isDebugModeEnabled]);

  // Disable tab navigation on all elements except search inputs
  // BUT ONLY when no modals or dropdowns are open (home screen only)
  useEffect(() => {
    // Check if any modal or overlay is open
    const isAnyModalOpen =
      showVisitorModal ||
      showResidentModal ||
      showAddNoteModal ||
      showNotesHistoryModal ||
      !!confirmModal ||
      !!closeTabConfirmation;

    const disableTabNavigation = () => {
      if (!operationalViewRef.current) return;

      // If any modal is open, allow normal tab navigation
      if (isAnyModalOpen) {
        // Restore tab navigation by removing tabindex=-1 ONLY from dynamically added ones
        // (not from elements that have data-no-tab attribute)
        const elements = operationalViewRef.current.querySelectorAll('[tabindex="-1"]:not([data-no-tab])');
        elements.forEach((element) => {
          element.removeAttribute('tabindex');
        });
        return;
      }

      // Get all interactive elements
      const elements = operationalViewRef.current.querySelectorAll(
        'button, a, input:not([data-allow-tab]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      elements.forEach((element) => {
        // Skip search inputs (those with data-allow-tab attribute)
        if (element.hasAttribute('data-allow-tab')) return;

        // Skip elements that should never be in tab order (data-no-tab)
        if (element.hasAttribute('data-no-tab')) return;

        // Skip elements inside modals/dropdowns (they have role="dialog" or similar)
        if (element.closest('[role="dialog"], [role="menu"], [data-dropdown]')) return;

        // Set tabIndex to -1 to remove from tab order
        element.setAttribute('tabindex', '-1');
      });
    };

    // Run whenever modal state changes
    disableTabNavigation();

    // Use MutationObserver to handle dynamically added elements
    const observer = new MutationObserver(disableTabNavigation);
    if (operationalViewRef.current) {
      observer.observe(operationalViewRef.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => observer.disconnect();
  }, [showVisitorModal, showResidentModal, showAddNoteModal, showNotesHistoryModal, confirmModal, closeTabConfirmation]);

  // Use new hooks for real data
  const { customers, isLoading: isLoadingCustomers } = useOperationalCustomers();

  // Get open customer IDs for the event stream
  const openCustomerIds = useMemo(() => openTabs.map(tab => tab.customerId), [openTabs]);

  // Dedup ref for toast notifications (same approach as Layout.tsx)
  const seenToastEventsRef = useRef<Map<string, number>>(new Map());
  const TOAST_DEDUP_MS = 30000;

  const handleRawEventForToast = useCallback(async (event: any) => {
    if (event.event_type === 'door_opened' || event.event_type === 'door_closed') return;
    if (event.event_type === 'auto_check_in' || event.event_type === 'auto_check_out') return;
    if (!event.person_name && !event.vehicle_plate) return; // nothing to show without a name or plate

    // For LPR plate events, exclude timestamp from dedup key so the same plate
    // captured multiple times within TOAST_DEDUP_MS is shown only once.
    const key = event.vehicle_plate
      ? `lpr:${event.vehicle_plate}-${event.device_id || ''}`
      : `${event.person_name || ''}-${event.event_timestamp}-${event.device_id || ''}`;
    const now = Date.now();
    const seen = seenToastEventsRef.current;
    seen.forEach((ts, k) => { if (now - ts > TOAST_DEDUP_MS) seen.delete(k); });
    if (seen.has(key)) return;
    seen.set(key, now);

    const isManualEvent = event.event_type === 'manual_check_in' || event.event_type === 'manual_check_out';
    const isRemoteEvent = event.event_type === 'remote_door_trigger' || event.event_type === 'remote_user';

    // Backend doesn't always resolve a photo for RFID-badge events (only facial-match events
    // carry one inline). Fall back to the shared person-photo cache — a warm cache resolves
    // instantly; a cold cache is awaited briefly so the very first event for someone still
    // gets their photo instead of the fallback icon.
    let resolvedPhotoUrl = makeAbsolutePhotoUrl(event.face_photo_url) || undefined;
    if (!resolvedPhotoUrl && event.person_id) {
      resolvedPhotoUrl = await resolvePersonPhoto(event.person_id);
    }

    notify.accessEvent({
      personId: event.person_id,
      personName: event.person_name,
      deviceName: event.device_name,
      zoneName: event.zone_name,
      direction: event.direction,
      facePhotoUrl: resolvedPhotoUrl,
      eventType: isRemoteEvent ? 'remote_door_trigger' : (event.is_authorized ? 'authentication_success' : 'unauthorized'),
      timestamp: new Date(event.event_timestamp),
      customerName: event.customer_name,
      customerType: event.customer_type,
      unitName: event.unit_name,
      streetName: event.street_name,
      departmentName: event.department_name,
      personType: event.person_type,
      personRole: event.person_role,
      vehiclePlate: event.vehicle_plate,
      accessType: event.access_type,
      isManual: isManualEvent,
    });
  }, []);

  const {
    isConnected: isEventStreamConnected,
    generalEvents: rawGeneralEvents,
    getEventsForCustomer,
    getUnreadCount,
    markAsRead,
    reconnect: reconnectEventStream,
  } = useGlobalEventStream(openCustomerIds, true, handleRawEventForToast);

  // Get porteiro events for active customer
  const {
    porteiroEvents,
    activeShifts,
    isLoading: isLoadingPorteiro,
  } = usePorteiroEventStream(activeTabId, true);

  // Debug log porteiro events
  useEffect(() => {
    if (porteiroEvents.length > 0) {
      logger.info('operational.view', 'Porteiro events received', {
        count: porteiroEvents.length,
        activeTabId,
        firstEventType: porteiroEvents[0]?.type
      });
    }
  }, [porteiroEvents, activeTabId]);

  // Real-time visitors via WebSocket (JSON-RPC 2.0). Falls back to polling on failure.
  const {
    visitors: rawVisitors,
    allVisitors: allRawVisitors,  // ALL visitors across all pages (for exit detection)
    isLoading: isLoadingVisitors,
    refresh: refreshVisitors,
    lastRefreshTime,
    pagination,
    setPage,
    nextPage,
    prevPage,
    connectionStatus: visitorWsStatus,
    getWsStats,
    setStatusFilter: setWsStatusFilter,
    setSearchMode: setWsSearchMode,
    notes: wsNotes,
  } = useVisitorWebSocket(activeTabId, 0);

  // Visitor exit detection - Only runs in Electron app
  // TIER 1: Real-time polling (every 10 seconds, last 1 minute only)
  // TIER 2: Full scan (on refresh, last 24 hours)
  // TIER 3: Smart chunking (for visitors inside 48+ hours)

  // Map inside visitors to format needed by hook
  // CRITICAL: Use allRawVisitors (ALL pages) not rawVisitors (current page only)!
  const insideVisitorsForCheck = useMemo(() => {
    return allRawVisitors
      .filter(v => v.status === 'inside')
      .map(v => ({
        personId: v.personId,
        roleId: v.roleId,
        name: v.name,
        // Use last_entry_at (most recent entry) — NOT first_entry_at.
        // For repeat visitors (always-enabled), first_entry_at is from weeks ago,
        // causing exit detection to match stale exit-device events from previous visits.
        entryTime: v.last_entry_at || v.first_entry_at || v.role_created_at || new Date().toISOString(),
      }));
  }, [allRawVisitors]);

  // Process deferred face removals from exit devices (queued at checkout with a delay)
  useDeferredDeviceRemovals(activeTabId);

  // Clear visitors whose inside time ran out. The backend expiry sweep only queues
  // these into the CLOUD sync queue, which can't reach a LAN reader, and the deferred
  // queue above is filled at checkout only — so an overstayed visitor kept working
  // face access until someone cleared them by hand.
  useExpiredVisitorAutoRevoke(activeTabId);

  // Get exit device configurations (no dynamic hooks, just device info)
  const exitDeviceInfo = useMultiDeviceExitDetection(devices);

  // Unified exit detection for ALL exit devices
  const unifiedExitDetection = useUnifiedExitDetection({
    devices: exitDeviceInfo.supportedDevices,
    insideVisitors: insideVisitorsForCheck,
    // NOTE: intentionally NOT calling manualCheckOut here anymore. This hook polls the
    // raw Hikvision device API directly, which has no zone concept at all (no zone_id,
    // no entry_type — Hikvision only reports which physical reader fired). A device's
    // zone_direction is just its default zone assignment; the same device can serve
    // multiple readers mapped to different zones, so a raw hash+time match can never
    // be reliably attributed to the actual exit zone from this data alone. The backend
    // (SQS pipeline + ProcessVisitorStateTransition) already resolves the real zone for
    // every event and is what SSE + the access-log scan (useVisitorExitAutoRevoke) act
    // on — that zone-aware path is the sole authority for confirming exits. This handler
    // stays for visibility/debugging only.
    onVisitorExited: (personId, roleId, deviceId) => {
      logger.warn('operational.view', 'Raw device match detected (not auto-actioned — zone-blind signal)', {
        personId,
        roleId,
        deviceId,
      });
    },
    enabled: isElectron() && !!activeTabId, // Enabled for ALL users (not just dev mode)
    pollingInterval: 30000, // OPTIMIZED: 30 seconds for faster detection!
    cacheTTL: 15000, // REDUCED: 15 seconds for fresher data!
  });

  // Wrapper function: Refresh visitors WITH full exit scan
  const refreshVisitorsWithExitDetection = useCallback(async () => {
    try {
      // 1. Perform full exit scan FIRST (24-hour window, bypasses cache)
      await unifiedExitDetection.performFullScan();

      // 2. Then refresh visitor list (will show updated statuses)
      await refreshVisitors();
    } catch (error) {
      logger.error('operational.view', 'Refresh with exit detection failed', error);
      // Still refresh visitors even if scan fails
      await refreshVisitors();
    }
  }, [unifiedExitDetection, refreshVisitors]);

  // Auto-refresh with exit detection (every 60 seconds)
  useEffect(() => {
    if (!activeTabId) return;

    const intervalId = setInterval(() => {
      refreshVisitorsWithExitDetection();
    }, 60000); // 60 seconds

    return () => clearInterval(intervalId);
  }, [activeTabId, refreshVisitorsWithExitDetection]);

  // Track last event ID to prevent re-triggering for the same event (avoids render loop)
  const lastVisitorEventIdRef = useRef<string | null>(null);

  // Keep stable refs to refresh callbacks so effects don't need them as deps
  const refreshVisitorsWithExitDetectionRef = useRef(refreshVisitorsWithExitDetection);
  useEffect(() => {
    refreshVisitorsWithExitDetectionRef.current = refreshVisitorsWithExitDetection;
  }, [refreshVisitorsWithExitDetection]);

  const refreshVisitorsRef = useRef(refreshVisitors);
  useEffect(() => {
    refreshVisitorsRef.current = refreshVisitors;
  }, [refreshVisitors]);

  // Event-driven visitor refresh - refresh when relevant events occur
  useEffect(() => {
    if (!activeTabId) return;

    const events = getEventsForCustomer(activeTabId);
    if (events.length === 0) return;

    // Get the most recent event
    const latestEvent = events[0];

    // Skip if we already reacted to this exact event (prevents render-loop)
    if (lastVisitorEventIdRef.current === latestEvent.id) return;

    // Check if it's a visitor-related event that requires refresh
    const isVisitorEvent =
      latestEvent.type === 'entry' ||
      latestEvent.type === 'exit' ||
      latestEvent.personType === 'visitante' ||
      latestEvent.personType === 'service_provider';

    if (isVisitorEvent) {
      lastVisitorEventIdRef.current = latestEvent.id;

      // Entry events: just refresh the visitor list (no exit scan needed — much faster)
      // Exit events: full refresh with exit scan to pick up undetected exits
      // Passagem events must never trigger exit scan — skip even if type was misresolved to 'exit'
      const isExitEvent = latestEvent.type === 'exit' && latestEvent.direction !== 'passagem';

      const timeoutId = setTimeout(() => {
        if (isExitEvent) {
          refreshVisitorsWithExitDetectionRef.current();
        } else {
          refreshVisitorsRef.current();
        }
      }, 500); // reduced from 1000ms since we no longer trigger heavy exit scan for entries

      return () => clearTimeout(timeoutId);
    }
  }, [getEventsForCustomer, activeTabId]); // refresh callbacks intentionally via refs

  // Calculate the current note scope based on view and selected location
  const currentNoteScope: NoteScope | undefined = useMemo(() => {
    if (currentView === 'location-detail' && selectedLocation) {
      if (selectedLocation.type === 'unit') {
        return { unit_id: selectedLocation.id };
      } else if (selectedLocation.type === 'department') {
        return { department_id: selectedLocation.id };
      }
    }
    return undefined;
  }, [currentView, selectedLocation]);

  // Calculate scope info for display in AddNoteModal
  const noteScopeInfo = useMemo((): { type: 'global' | 'unit' | 'department'; name?: string } | undefined => {
    if (currentView === 'location-detail' && selectedLocation) {
      if (selectedLocation.type === 'unit') {
        return { type: 'unit', name: selectedLocation.name };
      } else if (selectedLocation.type === 'department') {
        return { type: 'department', name: selectedLocation.name };
      }
    }
    return { type: 'global' };
  }, [currentView, selectedLocation]);

  // Single source of truth for customer notes (also passed down to CustomerNotesBanner,
  // which used to mount its own independent instance of this hook — duplicating every fetch).
  const {
    notes: bannerNotes,
    loading: bannerNotesLoading,
    refreshing: bannerNotesRefreshing,
    createNote,
    concludeNote: bannerConcludeNote,
    refreshNotes,
  } = useCustomerNotes(activeTabId || undefined, currentNoteScope);

  // Global notes count derived from WebSocket — no extra fetch needed
  const globalNotes = useMemo(
    () => wsNotes.filter(n => !n.unit_id && !n.department_id && !n.person_id),
    [wsNotes],
  );

  // Check if porteiro is allowed to view/approve pending pre-registrations for the active customer
  const activePorteiroCanApprovePreRegistrations = useMemo((): boolean => {
    const activeCustomer = availableCustomers.find((c: any) => c.id === activeTabId);
    return !!(activeCustomer as any)?.porteiro_can_approve_pre_registrations; // default false
  }, [availableCustomers, activeTabId]);
  const canApprovePreRegistrations = !isPorteiroUser || activePorteiroCanApprovePreRegistrations;

  // Separate flag for resident self-registrations (Auto-Cadastro de Moradores) — a different
  // feature from the visitor pré-registros above, gated by its own customer setting.
  const activePorteiroCanApproveSelfRegistrations = useMemo((): boolean => {
    const activeCustomer = availableCustomers.find((c: any) => c.id === activeTabId);
    return !!(activeCustomer as any)?.porteiro_can_approve_self_registrations; // default false
  }, [availableCustomers, activeTabId]);
  const canApproveSelfRegistrations = !isPorteiroUser || activePorteiroCanApproveSelfRegistrations;

  // Load pre-registrations count (auto-refresh every 30 seconds)
  useEffect(() => {
    const loadPreRegistrationsCount = async () => {
      if (!activeTabId || !canApprovePreRegistrations) {
        setPreRegistrationsCount(0);
        return;
      }

      try {
        const { getPendingPreRegistrations } = await import('../../services/preRegistrationApi');
        const preRegs = await getPendingPreRegistrations(activeTabId);
        setPreRegistrationsCount(preRegs.length);
      } catch (error) {
        console.error('Error loading pre-registrations count:', error);
        setPreRegistrationsCount(0);
      }
    };

    // Load immediately
    loadPreRegistrationsCount();

    // Auto-refresh every 30 seconds
    const interval = setInterval(loadPreRegistrationsCount, 30000);

    return () => clearInterval(interval);
  }, [activeTabId, showPreRegistrationsPanel, canApprovePreRegistrations]); // Refresh when panel opens/closes

  // Load account requests count (auto-refresh every 60 seconds)
  useEffect(() => {
    const loadAccountRequestsCount = async () => {
      if (!activeTabId) { setAccountRequestsCount(0); return; }
      try {
        const { api: axApi } = await import('../../services/api');
        const resp = await axApi.get(`/customers/${activeTabId}/account-requests?status=pending`);
        setAccountRequestsCount(resp.data?.total || 0);
      } catch {
        setAccountRequestsCount(0);
      }
    };
    loadAccountRequestsCount();
    const interval = setInterval(loadAccountRequestsCount, 60000);
    return () => clearInterval(interval);
  }, [activeTabId, showAccountRequestsPanel]);

  // Load pending encomendas count (auto-refresh every 30 seconds)
  useEffect(() => {
    const loadEncomendaCount = async () => {
      if (!activeTabId) { setEncomendaPendingCount(0); return; }
      try {
        const r = await listEncomendas(activeTabId, { status: ['received', 'notified'] });
        setEncomendaPendingCount(r.data?.length || 0);
      } catch {
        setEncomendaPendingCount(0);
      }
    };
    loadEncomendaCount();
    const interval = setInterval(loadEncomendaCount, 30000);
    return () => clearInterval(interval);
    // Deliberately NOT keyed on currentView: the count belongs to the customer, not to whatever
    // screen is open. Listing it there tore down and rebuilt the 30s interval on every navigation
    // and fired an immediate extra GET /encomendas each time — which is why the request showed up
    // while browsing units, far from anything to do with encomendas.
  }, [activeTabId]);

  // Transform customers to Condo format for compatibility
  const allCondos: Condo[] = useMemo(() => {
    return customers.map((c: OperationalCustomer) => ({
      id: c.id,
      name: c.name,
      lastAccess: c.lastAccess,
      type: c.type,
      condoType: c.condoType,
      photo_url: c.photo_url,
      internal_code: c.internal_code,
      portaria_type: c.portaria_type,
      phase: c.phase,
    }));
  }, [customers]);

  // Restore saved tabs when customers data is available
  // If user has only 1 customer, auto-select it without tabs
  useEffect(() => {
    if (!isLoadingCustomers && customers.length > 0 && !tabsRestored) {
      // If user has exactly 1 customer, auto-select it and skip tabs
      if (customers.length === 1) {
        const singleCustomer = customers[0];
        const autoTab: OpenTab = {
          customerId: singleCustomer.id,
          customerName: singleCustomer.name,
          unreadCount: 0,
        };
        setOpenTabs([autoTab]);
        setActiveTabId(singleCustomer.id);
        setTabsRestored(true);
        logger.info('operational.view', 'Auto-selected single available customer', {
          customerName: singleCustomer.name,
        });
        return;
      }

      const savedTabs = loadSavedTabs();
      const savedActiveTab = loadSavedActiveTab();

      if (savedTabs.length > 0) {
        // Validate that saved tabs still exist in customers list
        // and update names in case they changed
        const validTabs = savedTabs
          .map(tab => {
            const customer = customers.find(c => c.id === tab.customerId);
            if (customer) {
              return {
                ...tab,
                customerName: customer.name, // Update name in case it changed
              };
            }
            return null;
          })
          .filter((tab): tab is OpenTab => tab !== null);

        if (validTabs.length > 0) {
          setOpenTabs(validTabs);

          // Restore active tab if it's still valid
          if (savedActiveTab && validTabs.some(t => t.customerId === savedActiveTab)) {
            setActiveTabId(savedActiveTab);
          } else {
            // Set first tab as active if saved active tab is invalid
            setActiveTabId(validTabs[0].customerId);
          }

          logger.debug('operational.view', 'Restored tabs from localStorage', {
            count: validTabs.length,
            activeTab: savedActiveTab,
          });
        }
      }

      setTabsRestored(true);
    }
  }, [customers, isLoadingCustomers, tabsRestored]);

  // Save open tabs to localStorage whenever they change
  useEffect(() => {
    if (tabsRestored) {
      try {
        localStorage.setItem(STORAGE_KEY_OPEN_TABS, JSON.stringify(openTabs));
      } catch (e) {
        logger.warn('operational.view', 'Failed to save tabs to localStorage');
      }
    }
  }, [openTabs, tabsRestored]);

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    if (tabsRestored && activeTabId) {
      try {
        localStorage.setItem(STORAGE_KEY_ACTIVE_TAB, activeTabId);
      } catch (e) {
        logger.warn('operational.view', 'Failed to save active tab to localStorage');
      }
    }
  }, [activeTabId, tabsRestored]);

  // Transform visitors for compatibility
  // CRITICAL: Preserve object references to prevent unnecessary re-renders
  const activeVisitors: Visitor[] = useMemo(() => {
    // Since OperationalVisitor and Visitor have the same shape, just cast the type
    // This preserves the object references from the smart merge in useOperationalVisitors
    return rawVisitors as Visitor[];
  }, [rawVisitors]);

  // Transform ALL visitors for counts and exit detection
  const activeAllVisitors: Visitor[] = useMemo(() => {
    return allRawVisitors as Visitor[];
  }, [allRawVisitors]);

  // Get events for active customer
  const recentEvents: Event[] = useMemo(() => {
    if (!activeTabId) return [];
    const events = getEventsForCustomer(activeTabId);
    return events.map((e: OperationalEvent) => ({
      id: e.id,
      type: e.type,
      description: e.description,
      location: e.location,
      time: e.time,
      timestamp: e.timestamp,
      status: e.status,
      customerId: e.customerId,
      personId: e.personId,
      personPhoto: e.personPhoto,
      departmentName: e.departmentName,
      unitName: e.unitName,
      streetName: e.streetName,
      personType: e.personType,
      personRole: e.personRole,
      vehiclePlate: e.vehiclePlate,
      vehicleId: e.vehicleId,
    }));
  }, [activeTabId, getEventsForCustomer]);

  // Guard: auto check-in visitors stuck in 'awaiting' despite having a real entry event
  // Fixes the SSE/SQS split-brain bug where the event panel shows entry but state stays awaiting_entry
  useAwaitingEntryGuard(activeTabId, activeAllVisitors, recentEvents, refreshVisitors, visitorWsStatus);

  // Transform general events - enrich with customer name from openTabs if not present
  // Also merge in porteiro events
  const generalEvents: Event[] = useMemo(() => {
    // Create a lookup map for customer ID -> customer name
    const customerNameMap = new Map<string, string>();
    openTabs.forEach(tab => {
      customerNameMap.set(tab.customerId, tab.customerName);
    });

    // Transform access events
    const accessEvents = rawGeneralEvents.map((e: OperationalEvent) => {
      // Use customerName from event if available, otherwise look up from openTabs
      const customerName = e.customerName || (e.customerId ? customerNameMap.get(e.customerId) : undefined);

      return {
        id: e.id,
        type: e.type,
        description: e.description,
        location: e.location,
        time: e.time,
        timestamp: e.timestamp,
        status: e.status,
        customerId: e.customerId,
        customerName,
        personId: e.personId,
        personPhoto: e.personPhoto,
        departmentName: e.departmentName,
        unitName: e.unitName,
        streetName: e.streetName,
        personType: e.personType,
        personRole: e.personRole,
        vehiclePlate: e.vehiclePlate,
        vehicleId: e.vehicleId,
        direction: e.direction,
      };
    });

    // Transform porteiro events to Event format
    const porteiroEventsMapped: Event[] = porteiroEvents.map((pe: PorteiroEvent) => ({
      id: pe.id,
      type: pe.type as any, // Map porteiro event types
      description: pe.description,
      location: pe.location,
      time: pe.time,
      timestamp: pe.timestamp,
      status: pe.status,
      customerId: pe.customerId,
      customerName: pe.customerId ? customerNameMap.get(pe.customerId) : undefined,
    }));

    // Merge and sort by timestamp (most recent first)
    const merged = [...accessEvents, ...porteiroEventsMapped];
    merged.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));

    // Debug logging
    if (porteiroEventsMapped.length > 0) {
      logger.info('operational.view', 'Merged porteiro events into general events', {
        porteiroEventsCount: porteiroEventsMapped.length,
        accessEventsCount: accessEvents.length,
        totalMerged: merged.length
      });
    }

    return merged;
  }, [rawGeneralEvents, porteiroEvents, openTabs]);

  // Get selected condo name for display
  const selectedCondoName = useMemo(() => {
    const activeTab = openTabs.find(t => t.customerId === activeTabId);
    return activeTab?.customerName || '';
  }, [openTabs, activeTabId]);

  // Get active customer type for menu customization
  const activeCustomerType = useMemo((): 'condominio' | 'empresa' => {
    const activeCondo = allCondos.find(c => c.id === activeTabId);
    return activeCondo?.type === 'empresa' ? 'empresa' : 'condominio';
  }, [allCondos, activeTabId]);

  // Get active condo type for condominios (apartamento or casas)
  const activeCondoType = useMemo((): 'apartamento' | 'casas' => {
    const activeCondo = allCondos.find(c => c.id === activeTabId);
    return activeCondo?.condoType || 'apartamento';
  }, [allCondos, activeTabId]);

  // Check if pets feature is enabled for the active customer
  const activePetsEnabled = useMemo((): boolean => {
    const activeCustomer = availableCustomers.find((c: any) => c.id === activeTabId);
    return !!(activeCustomer as any)?.condo_data?.pets_enabled;
  }, [availableCustomers, activeTabId]);

  // Check if condo employees ("funcionários") feature is enabled for the active customer
  const activeFuncionariosEnabled = useMemo((): boolean => {
    const activeCustomer = availableCustomers.find((c: any) => c.id === activeTabId);
    return !!(activeCustomer as any)?.condo_data?.funcionarios_enabled;
  }, [availableCustomers, activeTabId]);

  // Check if keys feature is enabled for the active customer
  const activeKeysEnabled = useMemo((): boolean => {
    const activeCustomer = availableCustomers.find((c: any) => c.id === activeTabId);
    return !!(activeCustomer as any)?.condo_data?.keys_enabled || !!(activeCustomer as any)?.empresa_data?.keys_enabled;
  }, [availableCustomers, activeTabId]);

  // Check if livro feature is enabled for the active customer
  const activeLivroEnabled = useMemo((): boolean => {
    const activeCustomer = availableCustomers.find((c: any) => c.id === activeTabId);
    return !!(activeCustomer as any)?.condo_data?.livro_enabled || !!(activeCustomer as any)?.empresa_data?.livro_enabled;
  }, [availableCustomers, activeTabId]);

  // Check if solicitações (resident credential requests) is enabled for the active customer
  const activeSolicitacoesEnabled = useMemo((): boolean => {
    const activeCustomer = availableCustomers.find((c: any) => c.id === activeTabId);
    return !!(activeCustomer as any)?.condo_data?.solicitacoes_enabled
      || !!(activeCustomer as any)?.empresa_data?.solicitacoes_enabled;
  }, [availableCustomers, activeTabId]);

  // Load pending solicitações count (auto-refresh every 60 seconds).
  // Skipped entirely unless the module is on AND the user may manage it — the endpoint is
  // role-gated to admin/supervisor/síndico, so for a porteiro this would be a 403 every minute.
  useEffect(() => {
    if (!activeTabId || !activeSolicitacoesEnabled || !canManageSolicitacoes) {
      setSolicitacoesPendingCount(0);
      return;
    }
    const loadSolicitacoesCount = async () => {
      try {
        const { solicitacoesService } = await import('../../services/solicitacoesService');
        const pending = await solicitacoesService.listSolicitacoes(activeTabId, 'pending');
        setSolicitacoesPendingCount(pending.length);
      } catch {
        setSolicitacoesPendingCount(0);
      }
    };
    loadSolicitacoesCount();
    const interval = setInterval(loadSolicitacoesCount, 60000);
    return () => clearInterval(interval);
  }, [activeTabId, activeSolicitacoesEnabled, canManageSolicitacoes, currentView]);

  // Check if time-spent / zone occupancy feature is enabled for the active customer
  const activeTimeSpentEnabled = useMemo((): boolean => {
    const activeCustomer = availableCustomers.find((c: any) => c.id === activeTabId);
    return !!(activeCustomer as any)?.condo_data?.time_spent_enabled
      || !!(activeCustomer as any)?.empresa_data?.time_spent_enabled;
  }, [availableCustomers, activeTabId]);

  // Check if common areas feature is enabled for the active customer
  const activeCommonAreasEnabled = useMemo((): boolean => {
    const activeCustomer = availableCustomers.find((c: any) => c.id === activeTabId);
    return !!(activeCustomer as any)?.condo_data?.common_areas_enabled;
  }, [availableCustomers, activeTabId]);

  // Check if normas internas feature is enabled for the active customer
  const activeNormasInternasEnabled = useMemo((): boolean => {
    const activeCustomer = availableCustomers.find((c: any) => c.id === activeTabId);
    return !!(activeCustomer as any)?.condo_data?.normas_internas_enabled;
  }, [availableCustomers, activeTabId]);

  // Check if ocorrencias feature is enabled for the active customer
  const activeOcorrenciasEnabled = useMemo((): boolean => {
    const activeCustomer = availableCustomers.find((c: any) => c.id === activeTabId);
    return !!(activeCustomer as any)?.condo_data?.ocorrencias_enabled;
  }, [availableCustomers, activeTabId]);

  // Check if mural de anúncios feature is enabled for the active customer (condo or empresa)
  const activeMuralAnunciosEnabled = useMemo((): boolean => {
    const activeCustomer = availableCustomers.find((c: any) => c.id === activeTabId);
    return !!(activeCustomer as any)?.condo_data?.mural_anuncios_enabled || !!(activeCustomer as any)?.empresa_data?.mural_anuncios_enabled;
  }, [availableCustomers, activeTabId]);

  // Check if porteiro is allowed to register persons for the active customer
  const activePorteiroCanRegisterPersons = useMemo((): boolean => {
    const activeCustomer = availableCustomers.find((c: any) => c.id === activeTabId);
    return (activeCustomer as any)?.porteiro_can_register_persons !== false; // default true
  }, [availableCustomers, activeTabId]);

  const canRegisterPersons = !isPorteiroUser || activePorteiroCanRegisterPersons;

  // Get selected customer object for modal (includes type for dynamic labels)
  const selectedCustomerForModal = useMemo(() => {
    const activeCondo = allCondos.find(c => c.id === activeTabId);
    if (!activeCondo) return null;
    const fullCustomer = availableCustomers.find((c: any) => c.id === activeTabId) as any;
    const mobileAppEnabled = !!(fullCustomer?.condo_data?.mobile_app_enabled || fullCustomer?.empresa_data?.mobile_app_enabled);
    return {
      type: activeCondo.type,
      name: activeCondo.name,
      condoType: activeCondo.condoType === 'casas' ? 'casa' : activeCondo.condoType,
      mobileAppEnabled,
    };
  }, [allCondos, activeTabId, availableCustomers]);

  // NOTE: SSE connection is managed globally by useGlobalEventStream

  // Set up the onVisitProfile callback for the PersonDetailModal
  // This allows the "Visitar Perfil" button to show the profile inline instead of navigating
  useEffect(() => {
    const handleVisitProfile = (personId: string) => {
      setSelectedProfilePersonId(personId);
      setCurrentView('person-profile');
    };

    setOnVisitProfile(handleVisitProfile);

    // Clean up when leaving operational mode
    return () => {
      setOnVisitProfile(null);
    };
  }, [setOnVisitProfile]);

  // Set up the onUnitClick callback to navigate directly to location-detail for that unit
  useEffect(() => {
    setOnUnitClick((unitId: string, unitName: string, blockId?: string) => {
      setSelectedLocation({ id: unitId, type: 'unit', name: unitName, parentBlockId: blockId });
      setPreviousView(currentView);
      setCurrentView('location-detail');
    });
    return () => { setOnUnitClick(null); };
  }, [setOnUnitClick, currentView]);

  // Deep-link support: consume a unit stashed in localStorage by another page (e.g.
  // PetDetailModal's "Unidade" click, which navigates to /dashboard to enter operational
  // mode) and jump straight to its location-detail view, same as onUnitClick above.
  useEffect(() => {
    const raw = localStorage.getItem('panther_operational_pending_unit');
    if (!raw) return;
    localStorage.removeItem('panther_operational_pending_unit');
    try {
      const pending = JSON.parse(raw) as { id: string; name: string; blockId?: string };
      if (pending?.id) {
        setSelectedLocation({ id: pending.id, type: 'unit', name: pending.name, parentBlockId: pending.blockId });
        setCurrentView('location-detail');
      }
    } catch {
      // ignore malformed pending unit
    }
    // Runs once on mount — this view is freshly entered whenever a pending unit exists.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set up the onDepartmentClick callback to navigate directly to location-detail for that department
  useEffect(() => {
    setOnDepartmentClick((departmentId: string, departmentName: string) => {
      setSelectedLocation({ id: departmentId, type: 'department', name: departmentName });
      setPreviousView(currentView);
      setCurrentView('location-detail');
    });
    return () => { setOnDepartmentClick(null); };
  }, [setOnDepartmentClick, currentView]);

  // Set up the onPersonUpdate callback to refresh visitors and residents when person data changes
  // Use useCallback to create stable handlePersonUpdate function
  const handlePersonUpdate = useCallback((personId: string) => {
    console.log(' OperationalView: handlePersonUpdate called for person:', personId);
    // Refresh visitors list to show updated person data (photos, names, etc.)
    console.log(' OperationalView: Calling refreshVisitors()');
    refreshVisitors();
    // Refresh residents list in QuickSearchBar
    setResidentsRefreshTrigger(prev => prev + 1);
    // Refresh cadastro data in MainContentArea (moradores, visitantes, etc.)
    setCadastroRefreshTrigger(prev => prev + 1);
  }, [refreshVisitors]);

  // Register the callback with PersonDetailContext
  // Use ref to avoid infinite loop - handlePersonUpdate can change but we only register once
  const handlePersonUpdateRef = useRef(handlePersonUpdate);
  useEffect(() => {
    handlePersonUpdateRef.current = handlePersonUpdate;
  }, [handlePersonUpdate]);

  useEffect(() => {
    // Wrapper that calls the current ref value
    const stableHandler = (personId: string) => {
      handlePersonUpdateRef.current(personId);
    };

    setOnPersonUpdate(stableHandler);

    // Clean up when leaving operational mode
    return () => {
      setOnPersonUpdate(null);
    };
  }, [setOnPersonUpdate]);

  // Raw devices/zones for the active customer — fetch is shared/de-duped across the app
  // via useCustomerDevicesAndZones (Layout's Botoeira widget needs this same data and used
  // to trigger its own independent duplicate fetch of the same two endpoints).
  const { devices: rawDevices, zones: rawZones, isLoading: isLoadingRawDevices } = useCustomerDevicesAndZones(activeTabId);

  useEffect(() => {
    setIsLoadingDevices(isLoadingRawDevices);
  }, [isLoadingRawDevices]);

  // Transform the raw API devices/zones into our Device type whenever they change
  useEffect(() => {
    if (!activeTabId) {
      setDevices([]);
      return;
    }

    // Create a map of zone_id -> entry_type for fallback
    const zoneEntryTypeMap: Record<string, string> = {};
    rawZones.forEach((z: any) => {
      zoneEntryTypeMap[z.id] = z.entry_type;
    });

    const apiDevices: Device[] = rawDevices.map((d: any) => {
      // Use zone_direction from API if available, otherwise lookup from zones
      const zoneDirection = d.zone_direction ||
        (d.assigned_zone_id ? zoneEntryTypeMap[d.assigned_zone_id] : undefined) ||
        (d.zone_id ? zoneEntryTypeMap[d.zone_id] : undefined);

      return {
        id: d.id,
        name: d.name,
        type: d.device_type === 'facial_recognition' ? 'facial' : 'door',
        status: d.status || 'offline',
        device_type: d.device_type,
        zone_id: d.assigned_zone_id || d.zone_id,
        zone_name: d.assigned_zone_name || d.zone_name,
        zone_direction: zoneDirection,
        customer_id: d.customer_id,
        ip_address: d.ip_address,
        local_ip_address: d.local_ip_address,
        port: d.port,
        username: d.username || 'admin', // Default username
        password: d.password || '', // Required field
        manufacturer: d.manufacturer,
        config: d.config,
        model: d.model,
        serial_number: d.serial_number,
        firmware_version: d.firmware_version,
        mac_address: d.mac_address,
        last_heartbeat: d.last_heartbeat,
      };
    });
    setDevices(apiDevices);
    logger.info('operational.view', 'Loaded devices from API', {
      deviceCount: apiDevices.length,
      zonesCount: Object.keys(zoneEntryTypeMap).length,
      devicesWithDirection: apiDevices.filter(d => d.zone_direction).length
    });
  }, [activeTabId, rawDevices, rawZones]);

  // Fetch the user's saved botoeira order for this customer from the backend
  // whenever the active tab changes. Save/reset update savedDeviceOrder directly
  // (optimistic), so no re-fetch is needed there.
  useEffect(() => {
    if (!activeTabId) {
      setSavedDeviceOrder(null);
      return;
    }
    let cancelled = false;
    loadDeviceOrder(activeTabId).then(order => {
      if (!cancelled) setSavedDeviceOrder(order);
    });
    return () => { cancelled = true; };
  }, [activeTabId]);

  // Apply the saved custom device order (if any) to the live device list
  const orderedDevices = useMemo(() => {
    if (!activeTabId || devices.length === 0) return devices;

    const savedOrder = savedDeviceOrder;
    if (!savedOrder || savedOrder.length === 0) return devices;

    // Create a map for quick lookup
    const deviceMap = new Map(devices.map(d => [String(d.id), d]));

    // Build ordered array based on saved order
    const ordered: Device[] = [];
    const unorderedIds = new Set(devices.map(d => String(d.id)));

    // Add devices in saved order (if they still exist)
    for (const deviceId of savedOrder) {
      const device = deviceMap.get(deviceId);
      if (device) {
        ordered.push(device);
        unorderedIds.delete(deviceId);
      }
    }

    // Append any new devices (not in saved order) at the end
    Array.from(unorderedIds).forEach(deviceId => {
      const device = deviceMap.get(deviceId);
      if (device) ordered.push(device);
    });

    return ordered;
  }, [devices, activeTabId, savedDeviceOrder]);

  // Hybrid device health check (Electron only: local IP → public IP → HTTP)
  const { healthMap } = useDeviceHealthCheck(devices as any, { interval: 10000 });

  const devicesWithLiveStatus = useMemo(() => {
    if (!isElectron() || healthMap.size === 0) return orderedDevices;
    return orderedDevices.map(d => {
      const health = healthMap.get(String(d.id));
      return health ? { ...d, status: health.status } : d;
    });
  }, [orderedDevices, healthMap]);

  const selectedDeviceHistoryLive = useMemo(() => {
    if (!selectedDeviceHistory || !isElectron()) return selectedDeviceHistory;
    const health = healthMap.get(String(selectedDeviceHistory.id));
    return health ? { ...selectedDeviceHistory, status: health.status } : selectedDeviceHistory;
  }, [selectedDeviceHistory, healthMap]);

  // Mark events as read when switching tabs and reset view to visitors
  useEffect(() => {
    if (activeTabId) {
      markAsRead(activeTabId);
      // Reset to visitors view when switching customers
      setCurrentView('visitors');
      setSelectedCadastro(null);
      setSelectedLocation(null);
    }
  }, [activeTabId, markAsRead]);

  // Fetch blocks for resident registration modal
  useEffect(() => {
    const fetchBlocks = async () => {
      if (!activeTabId) {
        setBlocks([]);
        setUnits([]);
        return;
      }

      try {
        const response = await fetchWithAuth(`/api/v1/customers/${activeTabId}/blocks`);
        if (response.ok) {
          const data = await response.json();
          setBlocks(data || []);
        } else {
          setBlocks([]);
        }
      } catch (error) {
        logger.error('operational.view', 'Failed to fetch blocks', error);
        setBlocks([]);
      }
    };

    fetchBlocks();
  }, [activeTabId]);

  // Handle block change for resident registration modal — fetch units for the selected block
  const handleBlockChange = useCallback(async (blockId: string) => {
    if (!blockId || !activeTabId) {
      setUnits([]);
      return;
    }
    try {
      const response = await fetchWithAuth(`/api/v1/blocks/${blockId}/units`);
      if (response.ok) {
        const data = await response.json();
        const unitsArray = Array.isArray(data) ? data : (data?.data || []);
        setUnits(unitsArray);
      } else {
        setUnits([]);
      }
    } catch {
      setUnits([]);
    }
  }, [activeTabId]);

  // Handle resident registration submission
  const handleResidentSubmit = useCallback(async (submitData: {
    formData: any;
    photo: any | null;
    documentFrontPhoto?: any | null;
    documentBackPhoto?: any | null;
    vehiclePhoto?: any | null;
    vehicleList?: Array<{ plate: string; model: string; color: string }>;
    note?: string;
    selectedUser: any | null;
    unitAssignment: { unitId: string; relationType: string; parentesco?: string; startDate: string } | null;
  }) => {
    if (!activeTabId) return;

    const { formData: data, photo, documentFrontPhoto, documentBackPhoto, vehiclePhoto, vehicleList, note, selectedUser, unitAssignment } = submitData;
    setIsCreatingResident(true);

    try {
      // Create new person
      const createPersonPayload = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email || undefined,
        phone: data.phone || undefined,
        cpf: data.cpf,
        rg: data.rg || undefined,
        date_of_birth: data.birth_date || undefined,
        birth_date: data.birth_date || undefined, // Backend accepts both date_of_birth and birth_date
        is_minor: data.is_minor || false, // Always send is_minor flag (default false)
        ...foreignerPayload(data),
        keycloak_user_id: selectedUser?.id || undefined,
      };

      const personResponse = await fetchWithAuth(
        `/api/v1/customers/${activeTabId}/people`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(createPersonPayload),
        }
      );

      if (!personResponse.ok) {
        const error = await personResponse.json();
        showError(error.message || error.error || 'Erro ao criar pessoa');
        return;
      }

      const personData = await personResponse.json();
      const personId = personData.data?.id || personData.id;

      // Create note if provided (BEFORE role creation) — same pattern as VisitorRegistrationModal
      if (note?.trim()) {
        try {
          await condoApi.createCustomerNote(activeTabId, note.trim(), { person_id: personId });
        } catch (noteError) {
          logger.error('operational.view', 'Failed to create note', noteError as Error, { personId });
          showWarning('Morador criado, mas nota não foi salva');
        }
      }

      // Create resident role
      const rolePayload: any = {
        role_type: 'resident',
        valid_from: new Date().toISOString(),
        is_authorized: false,
      };

      // Add unit metadata if assigned
      if (unitAssignment) {
        rolePayload.unit_id = unitAssignment.unitId;
        rolePayload.relation_type = unitAssignment.relationType;
        if (unitAssignment.parentesco) rolePayload.parentesco = unitAssignment.parentesco;
        rolePayload.valid_from = unitAssignment.startDate;
      }

      const roleResponse = await fetchWithAuth(
        `/api/v1/customers/${activeTabId}/people/${personId}/roles`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(rolePayload),
        }
      );

      if (!roleResponse.ok) {
        const error = await roleResponse.json();
        showError(error.error || 'Erro ao criar função de morador');
        return;
      }

      // Upload photo if captured
      if (photo) {
        try {
          const formData = new FormData();
          appendIdentityPhoto(formData, photo);
          formData.append('customer_id', activeTabId);

          const photoResponse = await fetchWithAuth(
            `/api/v1/identity/resident/${personId}/photo`,
            {
              method: 'POST',
              body: formData,
            }
          );

          if (!photoResponse.ok) {
            logger.warn('operational.view', 'Failed to upload photo', { personId });
            showWarning('Morador criado, mas houve erro ao enviar a foto');
          }
        } catch (photoError) {
          logger.error('operational.view', 'Error uploading photo', photoError as Error);
          showWarning('Morador criado, mas houve erro ao enviar a foto');
        }
      }

      // Upload document/vehicle photos (best-effort)
      const docUpdates: Record<string, string> = {};
      for (const [key, phot, filename] of [
        ['document_front', documentFrontPhoto, 'document_front.jpg'],
        ['document_back',  documentBackPhoto,  'document_back.jpg'],
        ['vehicle',        vehiclePhoto,       'vehicle_photo.jpg'],
      ] as [string, any, string][]) {
        if (phot?.blob) {
          try {
            const fd = new FormData();
            fd.append('photo', phot.blob, filename);
            fd.append('customer_id', activeTabId);
            fd.append('photo_type', key);
            const r = await api.post(`/people/${personId}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            const url = r.data?.data?.photo_url || r.data?.photo_url;
            if (url) docUpdates[`${key}_url`] = url;
          } catch { /* best effort */ }
        }
      }
      if (Object.keys(docUpdates).length > 0) {
        try { await condoApi.updatePerson(activeTabId, personId, docUpdates); } catch { /* best effort */ }
      }

      // Non-fatal: the resident is already committed, so a rejected vehicle POST must not
      // fail this flow — but it is reported after the success message instead of swallowed.
      const residentVehicleFailures = await createVehiclesForPerson(activeTabId, personId, 'resident', vehicleList);

      const successMsg = unitAssignment
        ? 'Morador criado e vinculado à unidade com sucesso!'
        : 'Morador criado com sucesso!';
      showSuccess(successMsg);
      const residentVehicleWarning = describeVehicleFailures(residentVehicleFailures);
      if (residentVehicleWarning) showError(residentVehicleWarning);

      // Close modal and refresh data
      setShowResidentModal(false);
      // Trigger refresh of residents in QuickSearchBar
      setResidentsRefreshTrigger(prev => prev + 1);
    } catch (error) {
      logger.error('operational.view', 'Error creating resident', error as Error);
      showError('Erro ao salvar morador');
    } finally {
      setIsCreatingResident(false);
    }
  }, [activeTabId, showSuccess, showError, showWarning]);

  // Handle event click: open person modal for person events, navigate to unit for vehicle events
  const handleEventClick = useCallback(async (event: Event) => {
    if (event.personId) {
      openPersonDetail(event.personId, false, !isLightTheme);
    } else if (event.vehicleId && activeTabId) {
      try {
        const res = await fetchWithAuth(`/api/v1/customers/${activeTabId}/vehicles/${event.vehicleId}`);
        if (res.ok) {
          const data = await res.json();
          const unitId = data.data?.unit_id;
          const unitName = event.unitName || data.data?.unit?.number || '';
          if (unitId) {
            setSelectedLocation({ id: unitId, type: 'unit', name: unitName });
            setPreviousView(currentView);
            setCurrentView('location-detail');
          }
        }
      } catch { /* ignore */ }
    }
  }, [openPersonDetail, isLightTheme, activeTabId, currentView]);

  // Handle register resident from QuickSearchBar (goes directly to form)
  const handleRegisterResidentFromSearch = useCallback(() => {
    setShowResidentModal(true);
    setResidentModalInitialUnit(null);
  }, []);

  // Fetch this customer's departments per tab (used by employee registration
  // AND to resolve department_id → name for department-linked encomendas).
  // Reset on every tab change so switching customers doesn't keep showing the
  // previous customer's departments while the new list loads.
  useEffect(() => {
    setEmployeeDepartments([]);
    if (!activeTabId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetchWithAuth(`/api/v1/customers/${activeTabId}/departments`);
        if (resp.ok && !cancelled) {
          const json = await resp.json();
          const raw = json.data ?? json.departments ?? json;
          setEmployeeDepartments(Array.isArray(raw) ? raw : []);
        }
      } catch { /* ignore — callers still work without departments */ }
    })();
    return () => { cancelled = true; };
  }, [activeTabId]);

  // Handle register employee (empresa) from QuickSearchBar or cadastro "Novo" button
  const handleRegisterEmployee = useCallback(() => {
    setShowEmployeeModal(true);
  }, []);

  // Submit new employee
  const handleEmployeeSubmit = useCallback(async (submitData: { formData: any; photo: any | null; documentFrontPhoto?: any | null; documentBackPhoto?: any | null; vehicleList?: Array<{ plate: string; model: string; color: string }> }) => {
    if (!activeTabId) return;
    const { formData: data, photo, documentFrontPhoto, documentBackPhoto, vehicleList } = submitData;
    setIsCreatingEmployee(true);
    try {
      const resp = await fetchWithAuth(`/api/v1/customers/${activeTabId}/employees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email || undefined,
          phone: data.phone || undefined,
          cpf: data.cpf || undefined,
          rg: data.rg || undefined,
          date_of_birth: data.birth_date || undefined,
          is_minor: data.is_minor || false,
          ...foreignerPayload(data),
          position: data.position || undefined,
          department_ids: data.department_ids?.length ? data.department_ids : undefined,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json();
        showError(err.error || 'Erro ao criar funcionário');
        return;
      }
      const personData = await resp.json();
      const employeeId = personData.data?.id || personData.id;

      if (photo && employeeId) {
        try {
          const fd = new FormData();
          appendIdentityPhoto(fd, photo);
          fd.append('customer_id', activeTabId);
          const photoResponse = await fetchWithAuth(`/api/v1/identity/staff/${employeeId}/photo`, { method: 'POST', body: fd });
          if (!photoResponse.ok) {
            logger.warn('operational.view', 'Failed to upload employee photo', { employeeId });
            showWarning('Funcionário criado, mas houve erro ao enviar a foto');
          }
        } catch (photoError) {
          logger.error('operational.view', 'Error uploading employee photo', photoError as Error);
          showWarning('Funcionário criado, mas houve erro ao enviar a foto');
        }
      }

      // Upload document photos (best-effort) — same generic /people/:id/photos endpoint
      // used for residents; employeeId returned above IS the person's id.
      // Declared out here because the vehicle warning is emitted alongside the success
      // message, below the `if (employeeId)` block the vehicles are created inside.
      let employeeVehicleFailures: VehicleCreateFailure[] = [];

      if (employeeId) {
        const docUpdates: Record<string, string> = {};
        for (const [key, phot, filename] of [
          ['document_front', documentFrontPhoto, 'document_front.jpg'],
          ['document_back',  documentBackPhoto,  'document_back.jpg'],
        ] as [string, any, string][]) {
          if (phot?.blob) {
            try {
              const fd = new FormData();
              fd.append('photo', phot.blob, filename);
              fd.append('customer_id', activeTabId);
              fd.append('photo_type', key);
              const r = await api.post(`/people/${employeeId}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
              const url = r.data?.data?.photo_url || r.data?.photo_url;
              if (url) docUpdates[`${key}_url`] = url;
            } catch { /* best effort */ }
          }
        }
        if (Object.keys(docUpdates).length > 0) {
          try { await condoApi.updatePerson(activeTabId, employeeId, docUpdates); } catch { /* best effort */ }
        }

        // Non-fatal: the employee is already committed, so a rejected vehicle POST must not
        // fail this flow — but it is reported after the success message instead of swallowed.
        employeeVehicleFailures = await createVehiclesForPerson(activeTabId, employeeId, 'employee', vehicleList);
      }

      showSuccess('Funcionário criado com sucesso!');
      const employeeVehicleWarning = describeVehicleFailures(employeeVehicleFailures);
      if (employeeVehicleWarning) showError(employeeVehicleWarning);
      setShowEmployeeModal(false);
      setResidentsRefreshTrigger(prev => prev + 1);
    } catch (err: any) {
      showError(err.message || 'Erro ao criar funcionário');
    } finally {
      setIsCreatingEmployee(false);
    }
  }, [activeTabId, showSuccess, showError, showWarning]);

  // Handle register resident from ActionMenuBar (goes directly to form)
  const handleRegisterResidentFromAction = useCallback(() => {
    if (shouldUseMainFrame()) {
      setRegistrationContext({ type: 'resident' });
      setCurrentView('register-resident');
    } else {
      setShowResidentModal(true);
      setResidentModalInitialUnit(null);
    }
  }, [shouldUseMainFrame]);

  // Handle register resident from LocationDetailView (with unit pre-filled)
  const handleRegisterResidentFromLocation = useCallback((unitId: string, blockId: string) => {
    if (shouldUseMainFrame()) {
      setRegistrationContext({ type: 'resident', initialUnit: { unitId, blockId } });
      setCurrentView('register-resident');
    } else {
      setResidentModalInitialUnit({ unitId, blockId });
      setShowResidentModal(true);
    }
  }, [shouldUseMainFrame]);

  // Handle register visitor from LocationDetailView (with unit pre-filled)
  const handleRegisterVisitorFromLocation = useCallback((unitId: string, blockId: string) => {
    if (shouldUseMainFrame()) {
      setShowVisitorModal(false); // Close modal version if open
      setRegistrationContext({
        type: 'visitor',
        initialUnit: { unitId, blockId },
        initialData: { initialStep: 'form' }
      });
      setCurrentView('register-visitor');
    } else {
      setVisitorModalInitialUnit({ unitId, blockId });
      setVisitorModalInitialStep('form'); // Skip search step when coming from unit page
      setShowVisitorModal(true);
    }
  }, [shouldUseMainFrame]);

  // Handle revoke visitor access — opens RevokeReasonModal
  const handleRevokeVisitorAccess = useCallback((visitor: Visitor) => {
    if (!activeTabId || !visitor.personId || !visitor.roleId) return;
    setRevokeModal({ isOpen: true, visitor });
  }, [activeTabId]);

  // Called by RevokeReasonModal on confirm
  const handleRevokeConfirm = useCallback(async (reason: string) => {
    const { visitor } = revokeModal;
    if (!visitor || !activeTabId) return;
    try {
      // The reason now flows into the revoke call itself (recorded in the tamper-
      // evident access ledger with the actor and timestamp) — the CustomerNote below
      // is kept as a human-readable duplicate for the existing notes UI, not the
      // authoritative record anymore.
      // Fetch devices BEFORE revoke — zones are empty after backend revokes access
      const devices = await fetchPersonDevices(activeTabId, visitor.personId!);

      await condoApi.revokePersonRoleAuthorization(activeTabId, visitor.personId!, visitor.roleId!, reason.trim() || undefined);
      if (reason.trim()) {
        await condoApi.createCustomerNote(
          activeTabId,
          `[Revogação] ${reason}`,
          { person_id: visitor.personId! }
        );
      }

      // Remove from pre-fetched devices (fire-and-forget); failures persist to
      // localStorage and are retried automatically instead of being lost.
      revokeExitDevices(activeTabId, visitor.personId!, visitor.name, devices, 3).catch(err =>
        logger.error('operational.view', 'Device removal failed', err)
      );

      showSuccess('Acesso revogado com sucesso!');
      refreshVisitors();
      setRevokeModal({ isOpen: false, visitor: null });
    } catch (error) {
      logger.error('operational.view', 'Failed to revoke access', error);
      showError('Erro ao revogar acesso');
    }
  }, [revokeModal, activeTabId, showSuccess, showError, refreshVisitors]);

  // Manually approve a visitor left "pending_approval" (waiting on the app) on behalf of the
  // responsible resident/staff — e.g. they're unreachable and porteiro has verified in person.
  // The action still goes through the normal authorize endpoint, so it's captured by the same
  // audit trail as any other authorization — the modal just makes that explicit up front.
  const handleApproveVisitorAccess = useCallback((visitor: Visitor) => {
    if (!activeTabId || !visitor.personId || !visitor.roleId) return;

    setConfirmModal({
      isOpen: true,
      title: 'Aprovar Acesso Manualmente',
      message: `Você está aprovando o acesso de ${visitor.name} em nome de ${visitor.authorized_by_name || 'responsável'}, sem aguardar a confirmação pelo app. Esta ação será registrada no histórico. Deseja continuar?`,
      confirmText: 'Aprovar Acesso',
      confirmButtonClass: 'bg-green-600 hover:bg-green-700',
      icon: 'entry',
      isProcessing: false,
      showDisclaimer: false,
      disclaimerType: 'general',
      operatorName: operatorName,
      requireAcknowledgment: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isProcessing: true }));
        try {
          await condoApi.authorizePersonRole(activeTabId, visitor.personId!, visitor.roleId!);
          await condoApi.createCustomerNote(
            activeTabId,
            `[Aprovação manual] Acesso liberado por operador em nome de ${visitor.authorized_by_name || 'responsável'}, sem confirmação pelo app.`,
            { person_id: visitor.personId! }
          );
          notify.confirm('Acesso aprovado com sucesso', 'success');
          refreshVisitors();
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        } catch (error) {
          logger.error('operational.view', 'Failed to manually approve visitor', error);
          showError('Erro ao aprovar acesso');
        } finally {
          setConfirmModal(prev => ({ ...prev, isProcessing: false }));
        }
      },
    });
  }, [activeTabId, showError, refreshVisitors, operatorName]);

  // Handle manual check-in (Entrada Manual)
  const handleManualCheckIn = useCallback(async (visitor: Visitor) => {
    if (!activeTabId || !visitor.personId || !visitor.roleId) return;

    setConfirmModal({
      isOpen: true,
      title: 'Entrada Manual',
      message: `Registrar entrada manual para ${visitor.name}? Isto marcará o visitante como "dentro".`,
      confirmText: 'Registrar Entrada',
      confirmButtonClass: 'bg-green-600 hover:bg-green-700',
      icon: 'entry',
      isProcessing: false,
      showDisclaimer: false,
      disclaimerType: 'entry',
      operatorName: operatorName,
      requireAcknowledgment: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isProcessing: true }));
        try {
          await condoApi.manualCheckIn(activeTabId, visitor.personId, visitor.roleId);
          notify.confirm('Entrada Manual Registrada com Sucesso', 'success');
          refreshVisitors();
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        } catch (error) {
          logger.error('operational.view', 'Failed to check-in visitor', error);
          showError('Erro ao registrar entrada');
        } finally {
          setConfirmModal(prev => ({ ...prev, isProcessing: false }));
        }
      },
    });
  }, [activeTabId, showError, refreshVisitors, operatorName]);

  // Handle manual check-out (Saída)
  const handleManualCheckOut = useCallback(async (visitor: Visitor) => {
    if (!activeTabId || !visitor.personId || !visitor.roleId) return;

    setConfirmModal({
      isOpen: true,
      title: 'Registrar Saída',
      message: `Registrar saída de ${visitor.name}?`,
      confirmText: 'Registrar Saída',
      confirmButtonClass: 'bg-blue-600 hover:bg-blue-700',
      icon: 'exit',
      isProcessing: false,
      showDisclaimer: false,
      disclaimerType: 'general',
      operatorName: operatorName,
      requireAcknowledgment: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isProcessing: true }));
        try {
          // Check-out the visitor (backend automatically revokes access)
          await condoApi.manualCheckOut(activeTabId, visitor.personId, visitor.roleId);

          notify.confirm('Saída Manual Registrada com Sucesso', 'info');
          refreshVisitors();
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        } catch (error) {
          logger.error('operational.view', 'Failed to check-out visitor', error);
          showError('Erro ao registrar saída');
        } finally {
          setConfirmModal(prev => ({ ...prev, isProcessing: false }));
        }
      },
    });
  }, [activeTabId, showError, refreshVisitors, operatorName]);

  // Helper to select a customer/condo
  const handleSelectCondo = useCallback((condo: Condo) => {
    // Check if already in tabs
    const existingTab = openTabs.find(t => t.customerId === condo.id);

    if (existingTab) {
      // Just switch to that tab
      setActiveTabId(condo.id);
    } else {
      // Add new tab
      const newTab: OpenTab = {
        customerId: condo.id,
        customerName: condo.name,
        unreadCount: 0,
      };
      setOpenTabs(prev => [...prev, newTab]);
      setActiveTabId(condo.id);
    }

    setCondoSelectorOpen(false);
    setCondoSearchTerm('');
  }, [openTabs]);

  // Handle closing a tab
  const handleCloseTab = useCallback((customerId: string) => {
    setOpenTabs(prev => {
      const newTabs = prev.filter(t => t.customerId !== customerId);

      // If we closed the active tab, switch to another
      if (activeTabId === customerId && newTabs.length > 0) {
        setActiveTabId(newTabs[newTabs.length - 1].customerId);
      } else if (newTabs.length === 0) {
        setActiveTabId(null);
      }

      return newTabs;
    });
  }, [activeTabId]);

  // Handle reordering tabs (drag and drop)
  const handleReorderTabs = useCallback((reorderedTabs: OpenTab[]) => {
    setOpenTabs(reorderedTabs);
  }, []);

  // For backward compatibility with existing components that use string[] openTabs
  const openTabNames = useMemo(() => openTabs.map(t => t.customerName), [openTabs]);

  // Memoized callbacks for TopBar
  const handleSetSelectedCondoByName = useCallback((name: string) => {
    const condo = allCondos.find(c => c.name === name);
    if (condo) handleSelectCondo(condo);
  }, [allCondos, handleSelectCondo]);

  const handleSetOpenTabsStub = useCallback((tabs: string[]) => {
    // This is mainly for backward compatibility, actual tab management happens in handleSelectCondo
  }, []);

  const handleShowNotesHistory = useCallback(() => {
    setShowNotesHistoryModal(true);
  }, []);

  const handleShowAddNote = useCallback(() => {
    setShowAddNoteModal(true);
  }, []);

  const handleDeviceAction = useCallback(async (deviceId: string, action: string) => {
    const device = devices.find(d => d.id === deviceId);

    if (action === 'trigger') {
      // Start loading state
      setDeviceStates(prev => ({ ...prev, [deviceId]: { state: 'loading', action: 'trigger' } }));
      setActiveDeviceMenu(null); // Close menu immediately

      try {
        await triggerDeviceDoor(
          { ...device!, id: String(device!.id) },
          fetchWithAuth,
        );
        setDeviceStates(prev => ({ ...prev, [deviceId]: { state: 'success', action: 'trigger' } }));
        hotToast.success(`${device?.name || 'Dispositivo'} acionado com sucesso!`);
      } catch (error) {
        logger.error('operational.view', 'Error triggering device', error);
        setDeviceStates(prev => ({ ...prev, [deviceId]: { state: 'error', action: 'trigger' } }));
        const msg = error instanceof Error ? error.message : `Erro ao acionar ${device?.name || 'dispositivo'}`;
        hotToast.error(msg);
      }

      // After 3 seconds, return to idle
      setTimeout(() => {
        setDeviceStates(prev => {
          const newStates = { ...prev };
          delete newStates[deviceId];
          return newStates;
        });
      }, 3000);

    } else if (action === 'test') {
      // Start loading state for test
      setDeviceStates(prev => ({ ...prev, [deviceId]: { state: 'loading', action: 'test' } }));
      setActiveDeviceMenu(null); // Close menu immediately

      try {
        // Use correct endpoint based on device type
        let endpoint: string;
        if (device?.device_type === 'rfid_reader') {
          endpoint = `/api/v1/iot-connect/rfid/devices/${deviceId}/test/connection`;
        } else {
          endpoint = `/api/v1/iot-connect/facial-recognition/devices/${deviceId}/test/connection`;
        }

        const response = await fetchWithAuth(endpoint, {
          method: 'GET'
        });

        if (response.ok) {
          setDeviceStates(prev => ({ ...prev, [deviceId]: { state: 'success', action: 'test' } }));
          showSuccess(`${device?.name || 'Dispositivo'} está funcionando!`);
        } else {
          setDeviceStates(prev => ({ ...prev, [deviceId]: { state: 'error', action: 'test' } }));
          showWarning(`${device?.name || 'Dispositivo'} com problemas`);
        }
      } catch (error) {
        logger.error('operational.view', 'Error testing device', error);
        setDeviceStates(prev => ({ ...prev, [deviceId]: { state: 'error', action: 'test' } }));
        showError(`Erro ao testar ${device?.name || 'dispositivo'}`);
      }

      // After 3 seconds, return to idle
      setTimeout(() => {
        setDeviceStates(prev => {
          const newStates = { ...prev };
          delete newStates[deviceId];
          return newStates;
        });
      }, 3000);

    } else if (action === 'history') {
      // Show device history
      setSelectedDeviceHistory(device || null);
      setCurrentView('history');
      setActiveDeviceMenu(null);

    } else if (action === 'reboot') {
      setActiveDeviceMenu(null);
      const isRFID = device?.device_type === 'rfid_reader' || device?.device_type === 'controlid_rfid';
      const isControlID = device?.manufacturer?.toLowerCase().includes('control') || device?.device_type === 'controlid_face' || device?.device_type === 'controlid_rfid';
      const result = await deviceCommunicationService.rebootDevice({
        id: deviceId,
        ip_address: device?.ip_address,
        local_ip_address: device?.local_ip_address,
        username: device?.username || '',
        password: device?.password || '',
        vendor: isControlID ? 'controlid' : 'hikvision',
        port: device?.port?.toString(),
      }, isRFID);
      if (result.success) {
        showSuccess(`Reinício solicitado para ${device?.name || 'dispositivo'}`);
      } else {
        showError(`Erro ao reiniciar ${device?.name || 'dispositivo'}`);
      }
    }
  }, [devices, showSuccess, showError, showWarning]);

  // Handle device reorder (called on drag-drop)
  const handleReorderDevices = useCallback((reorderedDevices: Device[]) => {
    setPendingDeviceOrder(reorderedDevices);
  }, []);

  // Save device order to the backend (persisted per user, per customer)
  const handleSaveDeviceOrder = useCallback(async () => {
    if (!activeTabId || pendingDeviceOrder.length === 0) return;

    const deviceIds = pendingDeviceOrder.map(d => String(d.id));
    setIsDeviceEditMode(false);
    setPendingDeviceOrder([]);
    setSavedDeviceOrder(deviceIds); // optimistic — instant reorder feedback

    const ok = await saveDeviceOrder(activeTabId, deviceIds);
    if (ok) {
      showSuccess('Ordem dos dispositivos salva!');
    } else {
      showError('Falha ao salvar a ordem dos dispositivos. Tente novamente.');
    }
  }, [activeTabId, pendingDeviceOrder, showSuccess, showError]);

  // Cancel edit mode
  const handleCancelDeviceOrder = useCallback(() => {
    setIsDeviceEditMode(false);
    setPendingDeviceOrder([]);
  }, []);

  // Reset to default API order
  const handleResetDeviceOrder = useCallback(async () => {
    if (!activeTabId) return;

    setIsDeviceEditMode(false);
    setPendingDeviceOrder([]);
    setSavedDeviceOrder(null); // optimistic — instant reset feedback

    const ok = await clearDeviceOrder(activeTabId);
    if (ok) {
      showSuccess('Ordem dos dispositivos restaurada!');
    } else {
      showError('Falha ao restaurar a ordem dos dispositivos. Tente novamente.');
    }
  }, [activeTabId, showSuccess, showError]);

  // Toggle edit mode with validation
  const handleToggleEditMode = useCallback((enabled: boolean) => {
    if (enabled) {
      // Check if any device is in non-idle state
      const hasActiveActions = Object.keys(deviceStates).length > 0;
      if (hasActiveActions) {
        showWarning('Aguarde as ações dos dispositivos terminarem');
        return;
      }
      setPendingDeviceOrder(orderedDevices); // Initialize with current order
    }
    setIsDeviceEditMode(enabled);
  }, [deviceStates, orderedDevices, showWarning]);

  // ─── Memoized handlers for React.memo'd children (TopBar / ActionMenuBar /
  // QuickSearchBar / MainContentArea / EventsPanel) — without useCallback these
  // were fresh function references every render, defeating the memo boundaries. ───
  const handleOpenPersonDetail = useCallback(
    (personId: string) => openPersonDetail(personId, false, !isLightTheme),
    [openPersonDetail, isLightTheme]
  );

  const handleToggleDebugPanel = useCallback(() => {
    setShowDebugPanel(prev => !prev);
  }, []);

  const handleOpenExpiredVisitors = useCallback(() => {
    setCurrentView('expired-visitors');
  }, []);

  const handleOpenDeviceAudit = useCallback(() => {
    setCurrentView('device-audit');
  }, []);

  const handleShowZoneOccupancy = useCallback(() => {
    setPreviousView(currentView);
    setCurrentView('zone-occupancy');
  }, [currentView]);

  const handleShowAvisos = useCallback(() => {
    setPreviousView(currentView);
    setCurrentView('avisos');
  }, [currentView]);

  const handleShowHelpCenter = useCallback(() => {
    setPreviousView(currentView);
    setCurrentView('help-center');
  }, [currentView]);

  const handleShowManuais = useCallback(() => {
    setPreviousView(currentView);
    setCurrentView('manuais');
  }, [currentView]);

  const handleRegisterVisitorFromMenu = useCallback(() => {
    if (shouldUseMainFrame()) {
      setShowVisitorModal(false); // Close modal version if open
      setRegistrationContext({
        type: 'visitor',
        initialData: { initialStep: 'form' }
      });
      setCurrentView('register-visitor');
    } else {
      setVisitorModalInitialStep('search');
      setShowVisitorModal(true);
    }
  }, [shouldUseMainFrame]);

  const handleShowPreRegistrations = useCallback(() => {
    if (!canApprovePreRegistrations) return;
    setPreviousView(currentView);
    setCurrentView('pre-registrations');
  }, [currentView, canApprovePreRegistrations]);

  const handleShowAccountRequests = useCallback(() => {
    setShowAccountRequestsPanel(true);
  }, []);

  const handleShowEncomendas = useCallback(() => {
    setPreviousView(currentView);
    setCurrentView('encomendas');
  }, [currentView]);

  const handleShowLivro = useCallback(() => {
    setPreviousView(currentView);
    setCurrentView('livro');
  }, [currentView]);

  const handleShowSolicitacoes = useCallback(() => {
    setPreviousView(currentView);
    setCurrentView('solicitacoes');
  }, [currentView]);

  const handleShowAreasComuns = useCallback(() => {
    setPreviousView(currentView);
    setCurrentView('areas-comuns');
  }, [currentView]);

  const handleShowNormas = useCallback(() => {
    setPreviousView(currentView);
    setCurrentView('normas-internas');
  }, [currentView]);

  const handleShowOcorrencias = useCallback(() => {
    setPreviousView(currentView);
    setCurrentView('ocorrencias');
  }, [currentView]);

  const handleShowMuralAnuncios = useCallback(() => {
    setPreviousView(currentView);
    setCurrentView('mural-anuncios');
  }, [currentView]);

  const handleQuickSearchUnitClick = useCallback((unitId: string, unitName: string) => {
    // Switch to location-detail view and show the specific location
    const locationType = activeCustomerType === 'empresa' ? 'department' : 'unit';
    setSelectedLocation({ id: unitId, type: locationType, name: unitName });
    setPreviousView('visitors');
    setCurrentView('location-detail');
    setCadastrosMenuOpen(false);
  }, [activeCustomerType]);

  const handleRegisterVisitorFromSearch = useCallback((
    prefilledData: any,
    personId?: string,
    personData?: any
  ) => {
    if (shouldUseMainFrame()) {
      // Main frame mode
      setShowVisitorModal(false); // Close modal version if open
      setRegistrationContext({
        type: 'visitor',
        initialData: {
          ...prefilledData,
          initialStep: 'form'
        },
        prefilledPersonId: personData ? undefined : personId,
        prefilledPerson: personData ? {
          id: personData.id,
          first_name: personData.first_name,
          last_name: personData.last_name,
          cpf: personData.cpf,
          phone: personData.phone,
          identity_photo_url: personData.identity_photo_url,
          profile_picture_url: personData.profile_picture_url,
          document_front_url: personData.document_front_url,
          document_back_url: personData.document_back_url,
          vehicles: personData.vehicles,
        } : undefined
      });
      setCurrentView('register-visitor');
    } else {
      // Modal mode
      setVisitorModalInitialStep('form');
      setPrefilledVisitorData(prefilledData);

      // If full person data is provided (with photos), use it instead of just ID
      // IMPORTANT: Only set ONE of prefilledPerson OR prefilledPersonId, not both!
      if (personData) {
        console.log(' OperationalView: Using full person data with photos');
        setPrefilledVisitorPerson({
          id: personData.id,
          first_name: personData.first_name,
          last_name: personData.last_name,
          cpf: personData.cpf,
          phone: personData.phone,
          identity_photo_url: personData.identity_photo_url,
          profile_picture_url: personData.profile_picture_url,
          document_front_url: personData.document_front_url,
          document_back_url: personData.document_back_url,
          vehicles: personData.vehicles,
        });
        setPrefilledVisitorPersonId(undefined); // Don't use ID-based fetch
      } else {
        console.log(' OperationalView: Using person ID for fetch');
        setPrefilledVisitorPerson(undefined); // Don't use direct data
        setPrefilledVisitorPersonId(personId); // Use ID to trigger fetch
      }

      setShowVisitorModal(true);
    }
  }, [shouldUseMainFrame]);

  const handleMainContentDeviceClick = useCallback((deviceId: string) => {
    const device = devices.find(d => d.id === deviceId);
    if (device) {
      setPreviousView(currentView); // Save current view before switching
      setSelectedDeviceHistory(device);
      setCurrentView('history');
    }
  }, [devices, currentView]);

  const handleMainContentLocationClick = useCallback(
    (locationId: string, locationType: 'unit' | 'department' | 'block') => {
      setPreviousView(currentView);
      setSelectedLocation({ id: locationId, type: locationType });
      setCurrentView('location-detail');
    },
    [currentView]
  );

  const handleBackFromDeviceHistory = useCallback(() => {
    setSelectedDeviceHistory(null);
    setCurrentView(previousView);
  }, [previousView]);

  const handleResetCadastro = useCallback(() => {
    setSelectedCadastro(null);
    setCurrentView('visitors');
  }, []);

  const handleEventsNavigate = useCallback((path: string) => {
    if (path === '/eventos-gerais') {
      setPreviousView(currentView);
      setCurrentView('eventos-gerais');
    }
  }, [currentView]);

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div ref={operationalViewRef} className={`h-screen flex flex-col overflow-hidden ${isLightTheme ? 'op-light bg-gray-100' : 'bg-black'}`}>
        {/* Toast Notifications */}
        <ToastContainer toasts={toasts} onClose={removeNotification} />

      {/* Top Bar */}
      <div className="flex-shrink-0">
        <TopBar
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
          selectedCondo={selectedCondoName}
          setSelectedCondo={handleSetSelectedCondoByName}
          condoSelectorOpen={condoSelectorOpen}
          setCondoSelectorOpen={setCondoSelectorOpen}
          condoSearchTerm={condoSearchTerm}
          setCondoSearchTerm={setCondoSearchTerm}
          allCondos={allCondos}
          openTabs={openTabNames}
          setOpenTabs={handleSetOpenTabsStub}
          onExitOperationalMode={onExitOperationalMode}
          isLoading={isLoadingCustomers}
          isConnected={isEventStreamConnected}
          onSelectCondo={handleSelectCondo}
          showDebugPanel={showDebugPanel}
          onToggleDebugPanel={handleToggleDebugPanel}
          onOpenExpiredVisitors={handleOpenExpiredVisitors}
          onOpenDeviceAudit={handleOpenDeviceAudit}
          singleCustomerMode={customers.length === 1}
          isSearching={isSearchLoading}
          eventCount={rawGeneralEvents.length}
          lastEventDescription={rawGeneralEvents[0]?.description}
          lastEventTime={rawGeneralEvents[0]?.time}
          wsConnectionStatus={visitorWsStatus}
          getWsStats={getWsStats}
          timeSpentEnabled={activeTimeSpentEnabled}
          onShowZoneOccupancy={handleShowZoneOccupancy}
          onShowAvisos={handleShowAvisos}
          onShowHelpCenter={handleShowHelpCenter}
          onShowManuais={handleShowManuais}
        />
      </div>

      {/* Customer Tabs - Only show when user has multiple customers */}
      {customers.length > 1 && (
        <div className="flex-shrink-0">
          <CustomerTabs
            openTabs={openTabs}
            activeTabId={activeTabId}
            setActiveTabId={setActiveTabId}
            allCondos={allCondos}
            setCloseTabConfirmation={setCloseTabConfirmation}
            onCloseTab={handleCloseTab}
            getUnreadCount={getUnreadCount}
            onReorderTabs={handleReorderTabs}
          />
        </div>
      )}

      {/* Customer Notes Banner - Only show when there's an active tab */}
      {/* Shows hierarchical notes based on current location:
          - When viewing a unit (condominio): shows unit-specific notes
          - When viewing a department (empresa): shows department-specific notes
          - Otherwise: shows global customer-level notes */}
      {activeTabId && (
        <div className="flex-shrink-0">
          <CustomerNotesBanner
            customerId={activeTabId}
            onShowHistory={handleShowNotesHistory}
            onShowAddNote={handleShowAddNote}
            scope={currentNoteScope}
            refreshTrigger={notesRefreshTrigger}
            wsNotes={wsNotes}
            lightTheme={isLightTheme}
            notes={bannerNotes}
            loading={bannerNotesLoading}
            refreshing={bannerNotesRefreshing}
            concludeNote={bannerConcludeNote}
            refreshNotes={refreshNotes}
          />
        </div>
      )}

      {/* Lembretes banner — pinned reminders shared between all operators of this customer.
          Always mounted (not gated on showLembrete) so it can fetch and report its own count —
          it decides internally whether to render, forcing itself visible and non-dismissible
          the moment at least one lembrete exists. */}
      {activeTabId && (
        <LembretesBanner
          customerId={activeTabId}
          lightTheme={isLightTheme}
          open={showLembrete}
          onClose={() => setShowLembrete(false)}
          onCountChange={setLembreteCount}
        />
      )}

      {/* Close Tab Confirmation */}
      {closeTabConfirmation && (
        <CloseTabConfirmation
          closeTabConfirmation={closeTabConfirmation}
          setCloseTabConfirmation={setCloseTabConfirmation}
          openTabs={openTabs}
          setOpenTabs={setOpenTabs}
          activeTabId={activeTabId}
          setActiveTabId={setActiveTabId}
          onCloseTab={handleCloseTab}
        />
      )}

      {/* Main Content Area - Responsive padding with bottom nav clearance on mobile */}
      <div className="flex-1 flex flex-col overflow-hidden p-1.5 md:p-2 lg:p-3 min-h-0 pb-16 md:pb-2">
        {/* Action Menu Bar + Quick Search - only show when there is an active tab */}
        {activeTabId && (
          <div className={`flex-shrink-0 flex flex-col md:flex-row items-stretch md:items-center gap-1.5 md:gap-2 w-full${currentView !== 'visitors' ? ' mb-1.5 md:mb-2' : ''}`}>
            <div className="flex-shrink-0">
              <ActionMenuBar
                currentView={currentView}
                setCurrentView={setCurrentView}
                cadastrosMenuOpen={cadastrosMenuOpen}
                setCadastrosMenuOpen={setCadastrosMenuOpen}
                setSelectedCadastro={setSelectedCadastro}
                onRegisterVisitor={handleRegisterVisitorFromMenu}
                customerType={activeCustomerType}
                onShowAddNote={handleShowAddNote}
                onShowNotesHistory={handleShowNotesHistory}
                activeNotesCount={globalNotes.length}
                onShowPreRegistrations={canApprovePreRegistrations ? handleShowPreRegistrations : undefined}
                preRegistrationsCount={preRegistrationsCount}
                onShowAccountRequests={handleShowAccountRequests}
                accountRequestsCount={accountRequestsCount}
                onShowEncomendas={handleShowEncomendas}
                encomendaPendingCount={encomendaPendingCount}
                petsEnabled={activePetsEnabled}
                keysEnabled={activeKeysEnabled}
                livroEnabled={activeLivroEnabled}
                onShowLivro={handleShowLivro}
                solicitacoesEnabled={activeSolicitacoesEnabled && canManageSolicitacoes}
                onShowSolicitacoes={handleShowSolicitacoes}
                solicitacoesPendingCount={solicitacoesPendingCount}
                commonAreasEnabled={activeCommonAreasEnabled}
                onShowAreasComuns={handleShowAreasComuns}
                normasInternasEnabled={activeNormasInternasEnabled}
                onShowNormas={handleShowNormas}
                ocorrenciasEnabled={activeOcorrenciasEnabled}
                onShowOcorrencias={handleShowOcorrencias}
                muralAnunciosEnabled={activeMuralAnunciosEnabled}
                onShowMuralAnuncios={handleShowMuralAnuncios}
                lightTheme={isLightTheme}
              />
            </div>
            <div className="flex-1 min-w-0">
              <QuickSearchBar
              customerId={activeTabId}
              customerType={activeCustomerType}
              onPersonClick={handleOpenPersonDetail}
              onUnitClick={handleQuickSearchUnitClick}
              onRegisterVisitor={handleRegisterVisitorFromSearch}
              onRegisterResident={activeCustomerType !== 'empresa' && canRegisterPersons ? handleRegisterResidentFromSearch : undefined}
              onRegisterEmployee={activeCustomerType === 'empresa' && canRegisterPersons ? handleRegisterEmployee : undefined}
              residentsRefreshTrigger={residentsRefreshTrigger}
              onLoadingChange={setIsSearchLoading}
              />
            </div>
            {/* Nothing left to toggle once there's at least one lembrete — it's forced
                visible and non-dismissible by LembretesBanner itself at that point. */}
            {lembreteCount === 0 && (
              <div className="flex-shrink-0">
                <button
                  onClick={() => setShowLembrete(v => !v)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors whitespace-nowrap',
                    showLembrete
                      ? (isLightTheme ? 'bg-amber-100 border-amber-300 text-amber-800' : 'bg-amber-500/20 border-amber-500/40 text-amber-300')
                      : (isLightTheme ? 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50' : 'bg-gray-900 border-gray-700 text-gray-300 hover:bg-gray-800')
                  )}
                  title="Lembretes"
                >
                  <Pin size={14} className={showLembrete ? 'rotate-45' : ''} />
                  Lembrete
                </button>
              </div>
            )}
          </div>
        )}

        {/* Main Grid - Responsive: flex column on mobile, grid on desktop */}
        <div className="flex-1 flex flex-col lg:grid lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-5 gap-1 md:gap-1.5 min-h-0 overflow-hidden">
          {/* Main Content Panel - varies by currentView */}
          {currentView === 'pre-registrations' && activeTabId && canApprovePreRegistrations ? (
            /* Pre-Registrations View - Visitor pre-registration management in main frame */
            <div className={cn("lg:col-span-3 2xl:col-span-4 min-w-0 rounded-xl border min-h-0 flex flex-col overflow-hidden", isLightTheme ? "bg-white border-gray-200" : "bg-gray-950 border-gray-800")}>
              <PreRegistrationsPanel
                inline
                open
                lightTheme={isLightTheme}
                customerId={activeTabId}
                onClose={() => setCurrentView(previousView || 'visitors')}
                onNewRegistration={(token) => { setVisitorPreRegToken(token); setShowVisitorModal(true); }}
                onValidatePreRegistration={(preReg) => {
                  setCurrentView(previousView || 'visitors');
                  setVisitorModalInitialStep('form');
                  if (preReg.comment) setPreRegistrationComment(preReg.comment);
                  setPrefilledVisitorData({ firstName: preReg.first_name, lastName: preReg.last_name, cpf: preReg.cpf });
                  (window as any).__preRegPhoto = preReg.profile_picture_url || null;
                  (window as any).__preRegPhone = preReg.phone || '';
                  if (preReg.visiting_block_id && preReg.visiting_unit_id) {
                    setVisitorModalInitialUnit({ blockId: preReg.visiting_block_id, unitId: preReg.visiting_unit_id });
                    setVisitorModalInitialDepartmentId(undefined);
                  } else if (preReg.visiting_department_id) {
                    setVisitorModalInitialUnit(null);
                    setVisitorModalInitialDepartmentId(preReg.visiting_department_id);
                  } else {
                    setVisitorModalInitialUnit(null);
                    setVisitorModalInitialDepartmentId(undefined);
                  }
                  setPrefilledVisitorPerson(undefined);
                  setPrefilledVisitorPersonId(undefined);
                  setShowVisitorModal(true);
                }}
              />
            </div>
          ) : currentView === 'solicitacoes' && activeTabId ? (
            /* Solicitações — the síndico/admin queue for resident credential requests */
            <div className={cn(
              "lg:col-span-3 2xl:col-span-4 min-w-0 rounded-xl border min-h-0 flex flex-col overflow-hidden",
              isLightTheme ? "bg-white border-gray-200" : "bg-gray-950 border-gray-800"
            )}>
              <SolicitacoesPanel
                customerId={activeTabId}
                onBack={() => setCurrentView(previousView || 'visitors')}
                lightTheme={isLightTheme}
                condoType={activeCondoType === 'casas' ? 'casa' : activeCondoType}
                onOpenUnit={(unitId, unitName, blockId) => {
                  setSelectedLocation({ id: unitId, type: 'unit', name: unitName, parentBlockId: blockId });
                  setPreviousView('solicitacoes');
                  setCurrentView('location-detail');
                }}
                onOpenPerson={handleOpenPersonDetail}
              />
            </div>
          ) : currentView === 'livro' && activeTabId ? (
            /* Livro de Ocorrências View */
            <LivroPanel
              customerId={activeTabId}
              onClose={() => setCurrentView(previousView || 'visitors')}
              lightTheme={isLightTheme}
              // The portaria writes the book from here all shift; closing a page is the síndico's
              // call. Derived from the ROLE, not the surface — a síndico working in the
              // Operational View is still a síndico.
              canManage={canManageLivro(keycloakRoles)}
            />
          ) : currentView === 'encomendas' && activeTabId ? (
            /* Encomendas View - Package management in main frame */
            <div className="lg:col-span-3 2xl:col-span-4 min-w-0 bg-gray-950 rounded-xl border border-gray-800 min-h-0 flex flex-col overflow-hidden">
              <EncomendaPanel
                inline
                open
                customerId={activeTabId}
                customerType={activeCustomerType}
                condoType={activeCondoType === 'casas' ? 'casa' : activeCondoType}
                lightTheme={isLightTheme}
                departments={employeeDepartments}
                onClose={() => setCurrentView(previousView || 'visitors')}
                onOpenUnit={(unitId, unitName, blockId) => {
                  setSelectedLocation({ id: unitId, type: 'unit', name: unitName, parentBlockId: blockId });
                  setPreviousView('encomendas');
                  setCurrentView('location-detail');
                }}
                onPersonClick={handleOpenPersonDetail}
              />
            </div>
          ) : currentView === 'expired-visitors' && activeTabId ? (
            /* Expired Visitors View - List of expired visitors with bulk removal */
            <div className="lg:col-span-3 2xl:col-span-4 min-w-0 bg-gray-950 rounded-xl border border-gray-800 min-h-0 flex flex-col overflow-hidden">
              <ExpiredVisitorsView
                customerId={activeTabId}
                onBack={() => setCurrentView('visitors')}
                onPersonClick={(personId) => {
                  setSelectedProfilePersonId(personId);
                  setCurrentView('person-profile');
                }}
              />
            </div>
          ) : currentView === 'device-audit' && activeTabId ? (
            /* Device Audit View - Scan devices for registered visitors */
            <div className="lg:col-span-3 2xl:col-span-4 min-w-0 bg-gray-950 rounded-xl border border-gray-800 min-h-0 flex flex-col overflow-hidden">
              <DeviceAuditView
                customerId={activeTabId}
                onBack={() => setCurrentView('visitors')}
                onPersonClick={(personId) => {
                  setSelectedProfilePersonId(personId);
                  setCurrentView('person-profile');
                }}
              />
            </div>
          ) : currentView === 'eventos-gerais' ? (
            /* Eventos Gerais - Full events page embedded in OperationalView */
            <div className="lg:col-span-3 2xl:col-span-4 min-w-0 bg-gray-950 rounded-xl border border-gray-800 min-h-0 flex flex-col overflow-hidden">
              <Suspense fallback={
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-500"></div>
                </div>
              }>
                <EventosGerais
                  onBack={() => setCurrentView(previousView || 'visitors')}
                  customerId={activeTabId ?? undefined}
                  customerType={activeCustomerType}
                />
              </Suspense>
            </div>
          ) : currentView === 'zone-occupancy' && activeTabId ? (
            /* Zone Occupancy - Who is inside right now */
            <div className={cn(
              "lg:col-span-3 2xl:col-span-4 min-w-0 rounded-xl border min-h-0 flex flex-col overflow-hidden",
              isLightTheme ? "bg-white border-gray-200" : "bg-gray-950 border-gray-800"
            )}>
              <ZoneOccupancyView
                customerId={activeTabId}
                onBack={() => setCurrentView(previousView || 'visitors')}
                onPersonClick={handleOpenPersonDetail}
                lightTheme={isLightTheme}
              />
            </div>
          ) : currentView === 'areas-comuns' && activeTabId ? (
            /* Áreas Comuns - Common area reservations management */
            <div className={cn(
              "lg:col-span-3 2xl:col-span-4 min-w-0 rounded-xl border min-h-0 flex flex-col overflow-hidden",
              isLightTheme ? "bg-white border-gray-200" : "bg-gray-950 border-gray-800"
            )}>
              <AreasComuns
                customerId={activeTabId}
                onBack={() => setCurrentView(previousView || 'visitors')}
                lightTheme={isLightTheme}
              />
            </div>
          ) : currentView === 'normas-internas' && activeTabId ? (
            /* Normas Internas - Internal condo regulations */
            <div className={cn(
              "lg:col-span-3 2xl:col-span-4 min-w-0 rounded-xl border min-h-0 flex flex-col overflow-hidden",
              isLightTheme ? "bg-white border-gray-200" : "bg-gray-950 border-gray-800"
            )}>
              <NormasPanel
                customerId={activeTabId}
                onBack={() => setCurrentView(previousView || 'visitors')}
                lightTheme={isLightTheme}
              />
            </div>
          ) : currentView === 'ocorrencias' && activeTabId ? (
            /* Ocorrências - Incident reports with comment threads */
            <div className={cn(
              "lg:col-span-3 2xl:col-span-4 min-w-0 rounded-xl border min-h-0 flex flex-col overflow-hidden",
              isLightTheme ? "bg-white border-gray-200" : "bg-gray-950 border-gray-800"
            )}>
              <OcorrenciasPanel
                customerId={activeTabId}
                onBack={() => setCurrentView(previousView || 'visitors')}
                lightTheme={isLightTheme}
                allowTargeting
                condoType={activeCondoType === 'casas' ? 'casa' : activeCondoType}
                onOpenUnit={(unitId, unitName, blockId) => {
                  setSelectedLocation({ id: unitId, type: 'unit', name: unitName, parentBlockId: blockId });
                  setPreviousView('ocorrencias');
                  setCurrentView('location-detail');
                }}
              />
            </div>
          ) : currentView === 'mural-anuncios' && activeTabId ? (
            /* Mural de Anúncios - Síndico/admin notice board with optional comments */
            <div className={cn(
              "lg:col-span-3 2xl:col-span-4 min-w-0 rounded-xl border min-h-0 flex flex-col overflow-hidden",
              isLightTheme ? "bg-white border-gray-200" : "bg-gray-950 border-gray-800"
            )}>
              <MuralAnunciosPanel
                customerId={activeTabId}
                customerType={activeCustomerType}
                onBack={() => setCurrentView(previousView || 'visitors')}
                lightTheme={isLightTheme}
              />
            </div>
          ) : currentView === 'avisos' ? (
            /* Avisos e Atualizações - Changelog and release notes */
            <div className={cn(
              "lg:col-span-3 2xl:col-span-4 min-w-0 rounded-xl border min-h-0 flex flex-col overflow-hidden",
              isLightTheme ? "bg-white border-gray-200" : "bg-gray-950 border-gray-800"
            )}>
              <AvisosPanel
                onBack={() => setCurrentView(previousView || 'visitors')}
                lightTheme={isLightTheme}
              />
            </div>
          ) : currentView === 'help-center' ? (
            /* Central de Ajuda - Help center panel */
            <div className={cn(
              "lg:col-span-3 2xl:col-span-4 min-w-0 rounded-xl border min-h-0 flex flex-col overflow-hidden",
              isLightTheme ? "bg-white border-gray-200" : "bg-gray-950 border-gray-800"
            )}>
              <HelpCenterPanel
                onBack={() => setCurrentView(previousView || 'visitors')}
              />
            </div>
          ) : currentView === 'manuais' ? (
            /* Manuais e Documentos */
            <div className={cn(
              "lg:col-span-3 2xl:col-span-4 min-w-0 rounded-xl border min-h-0 flex flex-col overflow-hidden",
              isLightTheme ? "bg-white border-gray-200" : "bg-gray-950 border-gray-800"
            )}>
              <ManuaisPanel
                onBack={() => setCurrentView(previousView || 'visitors')}
                lightTheme={isLightTheme}
              />
            </div>
          ) : currentView === 'person-profile' && selectedProfilePersonId ? (
            /* Person Profile View - Full profile page rendered inline */
            <div className={cn(
              "lg:col-span-3 2xl:col-span-4 min-w-0 rounded-xl min-h-0 flex flex-col overflow-y-auto custom-scrollbar",
              isLightTheme
                ? "bg-white border border-gray-200"
                : "bg-gray-950 border border-gray-800"
            )}>
              <Suspense fallback={
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                </div>
              }>
                <PersonProfile
                  personId={selectedProfilePersonId}
                  embedded={true}
                  lightTheme={isLightTheme}
                  onBack={() => {
                    setCurrentView('visitors');
                    setSelectedProfilePersonId(null);
                  }}
                />
              </Suspense>
            </div>
          ) : currentView === 'register-visitor' && activeTabId ? (
            /* Visitor Registration View - Form rendered in main frame */
            <div className="lg:col-span-3 2xl:col-span-4 min-w-0 flex flex-col overflow-hidden">
              <VisitorRegistrationModal
                key={`visitor-registration-${activeTabId}-${currentView}`}
                open={true}
                onClose={() => {
                  setCurrentView('visitors');
                  setRegistrationContext(null);
                }}
                customerId={activeTabId}
                onSuccess={() => {
                  setCurrentView('visitors');
                  setRegistrationContext(null);
                }}
                darkMode={!isLightTheme}
                selectedCustomer={selectedCustomerForModal}
                initialStep={registrationContext?.initialData?.initialStep || 'form'}
                initialFormData={registrationContext?.initialData}
                prefilledPersonId={registrationContext?.prefilledPersonId}
                prefilledPerson={registrationContext?.prefilledPerson}
                initialBlockId={registrationContext?.initialUnit?.blockId}
                initialUnitId={registrationContext?.initialUnit?.unitId}
                embedded={true}
              />
            </div>

          ) : currentView === 'register-resident' && activeTabId ? (
            /* Resident Registration View - Form rendered in main frame */
            <div className="lg:col-span-3 2xl:col-span-4 min-w-0 flex flex-col overflow-hidden">
              <ResidentRegistrationModal
                isOpen={true}
                onClose={() => {
                  setCurrentView('visitors');
                  setRegistrationContext(null);
                }}
                onSubmit={async (data) => {
                  await handleResidentSubmit(data);
                  setCurrentView('visitors');
                  setRegistrationContext(null);
                }}
                blocks={blocks}
                units={units}
                onBlockChange={(blockId) => {
                  handleBlockChange(blockId);
                }}
                initialBlockId={registrationContext?.initialUnit?.blockId}
                initialUnitId={registrationContext?.initialUnit?.unitId}
                darkMode={!isLightTheme}
                customerId={activeTabId}
                embedded={true}
                condoType={activeCondoType === 'casas' ? 'casa' : activeCondoType}
              />
            </div>

          ) : currentView === 'location-detail' && selectedLocation && activeTabId ? (
            <LocationDetailView
              locationId={selectedLocation.id}
              locationType={selectedLocation.type}
              customerId={activeTabId}
              customerType={activeCustomerType}
              mobileAppEnabled={selectedCustomerForModal?.mobileAppEnabled}
              condoType={activeCondoType === 'casas' ? 'casa' : activeCondoType}
              petsEnabled={activePetsEnabled}
              funcionariosEnabled={activeFuncionariosEnabled}
              lightTheme={isLightTheme}
              onBack={() => {
                // If viewing a unit that came from a block, go back to block
                if (selectedLocation.type === 'unit' && selectedLocation.parentBlockId) {
                  setSelectedLocation({ id: selectedLocation.parentBlockId, type: 'block' });
                } else if (previousView === 'visitors') {
                  // Came from visitor card click — go back to visitors
                  setCurrentView('visitors');
                  setSelectedLocation(null);
                } else if (previousView === 'cadastro') {
                  // Came from a cadastro section (e.g. vehicles list) — go back to same section
                  setCurrentView('cadastro');
                  setSelectedLocation(null);
                } else {
                  // Default: go to cadastro blocks list
                  setCurrentView('cadastro');
                  setSelectedCadastro('unidades');
                  setSelectedLocation(null);
                }
              }}
              onPersonClick={handleOpenPersonDetail}
              onUnitClick={(unitId, unitName) => {
                // Navigate from block detail to unit detail, storing parent block ID and name
                setSelectedLocation({ id: unitId, type: 'unit', name: unitName, parentBlockId: selectedLocation.id });
              }}
              onAddResident={handleRegisterResidentFromLocation}
              onAddVisitor={handleRegisterVisitorFromLocation}
            />
          ) : (
            <MainContentArea
              currentView={currentView}
              selectedCadastro={selectedCadastro}
              selectedCondo={selectedCondoName}
              activeVisitors={activeVisitors}      // Current page
              allVisitors={activeAllVisitors}       // ALL visitors (for counts)
              selectedDeviceHistory={selectedDeviceHistoryLive}
              isLoadingVisitors={isLoadingVisitors}
              customerId={activeTabId}
              customerType={activeCustomerType}
              mobileAppEnabled={selectedCustomerForModal?.mobileAppEnabled}
              condoType={activeCondoType === 'casas' ? 'casa' : activeCondoType}
              cadastroRefreshTrigger={cadastroRefreshTrigger}
              lastRefreshTime={lastRefreshTime}
              pagination={pagination}
              onPageChange={setPage}
              onNextPage={nextPage}
              onPrevPage={prevPage}
              onPersonClick={handleOpenPersonDetail}
              onDeviceClick={handleMainContentDeviceClick}
              onLocationClick={handleMainContentLocationClick}
              onRevokeAccess={handleRevokeVisitorAccess}
              onApproveAccess={handleApproveVisitorAccess}
              onManualCheckIn={handleManualCheckIn}
              onManualCheckOut={handleManualCheckOut}
              onBackFromDeviceHistory={handleBackFromDeviceHistory}
              onDeviceAction={handleDeviceAction}
              onResetCadastro={handleResetCadastro}
              onRefreshVisitors={refreshVisitorsWithExitDetection}
              onRegisterResident={activeCustomerType !== 'empresa' && canRegisterPersons ? handleRegisterResidentFromAction : undefined}
              onRegisterEmployee={activeCustomerType === 'empresa' && canRegisterPersons ? handleRegisterEmployee : undefined}
              visitorWsStatus={visitorWsStatus}
              getWsStats={getWsStats}
              onStatusFilterChange={setWsStatusFilter}
              onSearchModeChange={setWsSearchMode}
              petsEnabled={activePetsEnabled}
              canApproveSelfRegistrations={canApproveSelfRegistrations}
              lightTheme={isLightTheme}
            />
          )}

          {/* Right column — Eventos (default) or Botoeira (layout: 'swapped'), per user preference.
              Same full-width column either way — Botoeira's own internal overflow bugs (missing
              min-w-0/truncate down the flex chain) were what made it look oversized, not the
              column width itself; capping the width just left a dead gray margin at the edge. */}
          <div className={`lg:col-span-1 min-h-0 min-w-0${isSwappedLayout ? ' lg:order-first' : ''}`}>
            {isSwappedLayout ? (
              <Botoeira
                orientation="vertical"
                devices={isDeviceEditMode && pendingDeviceOrder.length > 0 ? pendingDeviceOrder : devicesWithLiveStatus}
                activeDeviceMenu={activeDeviceMenu}
                setActiveDeviceMenu={setActiveDeviceMenu}
                setMenuPosition={setMenuPosition}
                deviceStates={deviceStates}
                isEditMode={isDeviceEditMode}
                onToggleEditMode={handleToggleEditMode}
                onReorderDevices={handleReorderDevices}
                onSaveOrder={handleSaveDeviceOrder}
                onCancelOrder={handleCancelDeviceOrder}
                onResetOrder={handleResetDeviceOrder}
                lightTheme={isLightTheme}
              />
            ) : (
              <EventsPanel
                generalEvents={generalEvents}
                recentEvents={recentEvents}
                customerEventsCollapsed={customerEventsCollapsed}
                setCustomerEventsCollapsed={setCustomerEventsCollapsed}
                generalEventsCollapsed={generalEventsCollapsed}
                setGeneralEventsCollapsed={setGeneralEventsCollapsed}
                isConnected={isEventStreamConnected}
                singleCustomerMode={openTabs.length <= 1}
                onEventClick={handleEventClick}
                onNavigate={handleEventsNavigate}
              />
            )}
          </div>
        </div>
      </div>

      {/* Bottom row — Botoeira (default) or Eventos (layout: 'swapped'), per user preference */}
      <div className={isSwappedLayout ? "flex-shrink-0 min-h-0" : "flex-shrink-0"}>
        {isSwappedLayout ? (
          <EventsPanel
            layout="horizontal"
            generalEvents={generalEvents}
            recentEvents={recentEvents}
            customerEventsCollapsed={customerEventsCollapsed}
            setCustomerEventsCollapsed={setCustomerEventsCollapsed}
            generalEventsCollapsed={generalEventsCollapsed}
            setGeneralEventsCollapsed={setGeneralEventsCollapsed}
            isConnected={isEventStreamConnected}
            singleCustomerMode={openTabs.length <= 1}
            onEventClick={handleEventClick}
            onNavigate={handleEventsNavigate}
          />
        ) : (
          <Botoeira
            devices={isDeviceEditMode && pendingDeviceOrder.length > 0 ? pendingDeviceOrder : devicesWithLiveStatus}
            activeDeviceMenu={activeDeviceMenu}
            setActiveDeviceMenu={setActiveDeviceMenu}
            setMenuPosition={setMenuPosition}
            deviceStates={deviceStates}
            isEditMode={isDeviceEditMode}
            onToggleEditMode={handleToggleEditMode}
            onReorderDevices={handleReorderDevices}
            onSaveOrder={handleSaveDeviceOrder}
            onCancelOrder={handleCancelDeviceOrder}
            onResetOrder={handleResetDeviceOrder}
            lightTheme={isLightTheme}
          />
        )}
      </div>

      {/* Device Action Menu */}
      {activeDeviceMenu !== null && menuPosition && (
        <DeviceActionMenu
          activeDeviceMenu={activeDeviceMenu}
          setActiveDeviceMenu={setActiveDeviceMenu}
          menuPosition={menuPosition}
          devices={devices}
          handleDeviceAction={handleDeviceAction}
        />
      )}

      {/* Visitor Registration Modal */}
      {activeTabId && (
        <VisitorRegistrationModal
          open={showVisitorModal}
          onClose={() => {
            setShowVisitorModal(false);
            setVisitorPreRegToken(undefined);
            setPrefilledVisitorData(undefined);
            setPrefilledVisitorPersonId(undefined);
            setPrefilledVisitorPerson(undefined);
            setVisitorModalInitialUnit(null);
            setVisitorModalInitialDepartmentId(undefined);
            setVisitorModalInitialStep('search'); // Reset to search step
            setPreRegistrationComment(undefined); // Clear pre-registration comment
          }}
          customerId={activeTabId}
          selectedCustomer={selectedCustomerForModal}
          onSuccess={() => {
            // Refresh visitors list immediately after successful registration
            refreshVisitors();
            setPrefilledVisitorData(undefined);
            setPrefilledVisitorPersonId(undefined);
            setPrefilledVisitorPerson(undefined);
            setVisitorModalInitialUnit(null);
            setVisitorModalInitialDepartmentId(undefined);
            setVisitorModalInitialStep('search'); // Reset to search step
            setPreRegistrationComment(undefined); // Clear pre-registration comment
          }}
          preRegistrationComment={preRegistrationComment}
          darkMode={!isLightTheme}
          initialStep={visitorPreRegToken ? 'form' : visitorModalInitialStep}
          initialFormData={prefilledVisitorData}
          prefilledPersonId={prefilledVisitorPersonId}
          prefilledPerson={prefilledVisitorPerson}
          initialBlockId={visitorModalInitialUnit?.blockId}
          initialUnitId={visitorModalInitialUnit?.unitId}
          initialDepartmentId={visitorModalInitialDepartmentId}
          compactMode={true}
          preRegistrationToken={visitorPreRegToken}
          onPreRegistrationCreated={() => {
            setShowVisitorModal(false);
            setVisitorPreRegToken(undefined);
          }}
        />
      )}

      {/* Employee Registration Modal */}
      {showEmployeeModal && (
        <EmployeeRegistrationModal
          isOpen={showEmployeeModal}
          onClose={() => setShowEmployeeModal(false)}
          onSubmit={handleEmployeeSubmit}
          departments={employeeDepartments}
          isLoading={isCreatingEmployee}
          customerId={activeTabId}
          darkMode={!isLightTheme}
        />
      )}

      {/* Resident Registration Modal */}
      {showResidentModal && (
        <ResidentRegistrationModal
          isOpen={showResidentModal}
          onClose={() => {
            setShowResidentModal(false);
            setResidentModalInitialUnit(null);
          }}
          onSubmit={handleResidentSubmit as any}
          blocks={blocks}
          units={units}
          onBlockChange={handleBlockChange}
          isLoading={isCreatingResident}
          darkMode={!isLightTheme}
          initialBlockId={residentModalInitialUnit?.blockId}
          initialUnitId={residentModalInitialUnit?.unitId}
          customerId={activeTabId}
          condoType={activeCondoType === 'casas' ? 'casa' : activeCondoType}
        />
      )}

      {/* Add Note Modal */}
      <AddNoteModal
        open={showAddNoteModal}
        onClose={() => setShowAddNoteModal(false)}
        onSubmit={async (content) => {
          await createNote(content);
          setNotesRefreshTrigger(prev => prev + 1);
        }}
        customerName={selectedCondoName}
        scopeInfo={noteScopeInfo}
      />

      {/* Notes History Modal */}
      <NotesHistoryModal
        open={showNotesHistoryModal}
        onClose={() => setShowNotesHistoryModal(false)}
        customerId={activeTabId || undefined}
        customerName={selectedCondoName}
        customerType={activeCustomerType}
        condoType={activeCondoType}
        lightTheme={isLightTheme}
      />

      {/* Pre-Registrations Panel */}
      <PreRegistrationsPanel
        open={showPreRegistrationsPanel}
        customerId={activeTabId || undefined}
        onClose={() => {
          setShowPreRegistrationsPanel(false);
          setPreRegistrationsCount(prev => prev);
        }}
        onNewRegistration={(token) => {
          setShowPreRegistrationsPanel(false);
          setVisitorPreRegToken(token);
          setShowVisitorModal(true);
        }}
        onValidatePreRegistration={(preReg) => {
          // Close pre-registrations panel
          setShowPreRegistrationsPanel(false);

          // Pre-fill visitor modal with pre-registration data (but keep ALL fields editable)
          setVisitorModalInitialStep('form');

          // Set pre-registration comment for banner
          if (preReg.comment) {
            setPreRegistrationComment(preReg.comment);
          }

          // ONLY use initialFormData - don't use prefilledPerson to avoid locking fields
          setPrefilledVisitorData({
            firstName: preReg.first_name,
            lastName: preReg.last_name,
            cpf: preReg.cpf,
          });

          // Store photo and phone in window object temporarily for the modal to access
          (window as any).__preRegPhoto = preReg.profile_picture_url || null;
          (window as any).__preRegPhone = preReg.phone || '';

          // Pre-fill block/unit or department if available
          if (preReg.visiting_block_id && preReg.visiting_unit_id) {
            setVisitorModalInitialUnit({
              blockId: preReg.visiting_block_id,
              unitId: preReg.visiting_unit_id,
            });
            setVisitorModalInitialDepartmentId(undefined);
          } else if (preReg.visiting_department_id) {
            setVisitorModalInitialUnit(null);
            setVisitorModalInitialDepartmentId(preReg.visiting_department_id);
          } else {
            setVisitorModalInitialUnit(null);
            setVisitorModalInitialDepartmentId(undefined);
          }

          // Don't use prefilledVisitorPerson - it locks the fields!
          setPrefilledVisitorPerson(undefined);
          setPrefilledVisitorPersonId(undefined);

          // Open visitor modal
          setShowVisitorModal(true);
        }}
      />

      {/* Account Requests Panel */}
      <AccountRequestsPanel
        open={showAccountRequestsPanel}
        customerId={activeTabId || undefined}
        onClose={() => setShowAccountRequestsPanel(false)}
      />

      {/* Encomendas Panel — rendered inline in main frame via currentView='encomendas' */}

      {/* Confirmation Modal */}
      <OperationalConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        confirmButtonClass={confirmModal.confirmButtonClass}
        icon={confirmModal.icon}
        isProcessing={confirmModal.isProcessing}
        showDisclaimer={confirmModal.showDisclaimer}
        disclaimerType={confirmModal.disclaimerType}
        operatorName={confirmModal.operatorName}
        requireAcknowledgment={confirmModal.requireAcknowledgment}
      />

      {/* Revoke Reason Modal */}
      <RevokeReasonModal
        isOpen={revokeModal.isOpen}
        visitor={revokeModal.visitor}
        operatorName={operatorName}
        onClose={() => setRevokeModal({ isOpen: false, visitor: null })}
        onConfirm={handleRevokeConfirm}
        darkMode={!isLightTheme}
      />

      {/* Exit Detection Debug Panel (Electron dev only) */}
      <ExitDetectionDebugPanel
        isOpen={showDebugPanel && isDebugModeEnabled}
        onClose={() => setShowDebugPanel(false)}
        logs={debugLogs}
        onClearLogs={clearDebugLogs}
        deviceStatuses={unifiedExitDetection.deviceStatuses}
        totalExitsDetected={unifiedExitDetection.totalExitsDetected}
        isChecking={unifiedExitDetection.isChecking}
        lastCheckTime={unifiedExitDetection.lastCheckTime}
        pollingInterval={30000}
        cacheStats={unifiedExitDetection.cacheStats}
        onClearCache={unifiedExitDetection.clearCache}
        onResetTimestamps={unifiedExitDetection.resetTimestamps}
      />
      </div>
    </>
  );
};

export default OperationalView;
